/*
 * Find Your Coffee (the Next.js app's src/components/journey/*, src/lib/journeys.ts and the brew
 * guides from src/lib/brew.ts), as one vanilla module for sections/find-your-coffee.liquid.
 *
 * Questions fill the screen one at a time with photo or icon answers. The rules (questions,
 * filters, points, ties) are a line-by-line port of journeys.ts, so a set of answers gives the
 * same two coffees as the app. The coffees come from the section's JSON data island (Shopify
 * products and their champions.* metafields); nothing about a coffee is hardcoded here.
 *
 * The answers live in the address (?for=office&setup=capsules...&done=1), so a result can be
 * shared and reopened. ?from=solutions skips "What best describes you?".
 * The result's lead form is a Shopify customer form in the Liquid, sent by champions.js
 * ("lead forms"); this file fills its hidden fields and listens for `champions:lead`.
 * Analytics: events go to window.dataLayer (components/journey/track.ts).
 */
(() => {
  "use strict";

  /* ------------------------------------------------------------------ helpers */
  const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
  const $$ = (sel, root) => Array.from(root.querySelectorAll(sel));
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  const reduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const track = (event, data = {}) => {
    (window.dataLayer = window.dataLayer || []).push({ event, ...data });
  };
  const animate = (el, frames, opts) => {
    if (!el || !el.animate) return Promise.resolve();
    const a = el.animate(frames, { easing: EASE, fill: "both", ...opts });
    return a.finished.then(
      () => {
        try {
          a.commitStyles && el.isConnected && a.commitStyles();
        } catch (e) {
          /* element hidden: nothing to keep */
        }
        a.cancel();
      },
      () => {},
    );
  };
  const capitalise = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  /* Lucide icons (lucide-react 1.48, the app's version): inner SVG markup. */
  const ICONS = {
    "check": "<path d=\"M20 6 9 17l-5-5\"/>",
    "arrow-left": "<path d=\"m12 19-7-7 7-7\"/><path d=\"M19 12H5\"/>",
    "arrow-right": "<path d=\"M5 12h14\"/><path d=\"m12 5 7 7-7 7\"/>",
    "rotate-ccw": "<path d=\"M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8\"/><path d=\"M3 3v5h5\"/>",
    "copy": "<rect width=\"14\" height=\"14\" x=\"8\" y=\"8\" rx=\"2\" ry=\"2\"/><path d=\"M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2\"/>",
    "mail": "<path d=\"m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7\"/><rect x=\"2\" y=\"4\" width=\"20\" height=\"16\" rx=\"2\"/>",
    "plus": "<path d=\"M5 12h14\"/><path d=\"M12 5v14\"/>",
    "sparkles": "<path d=\"M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z\"/><path d=\"M20 2v4\"/><path d=\"M22 4h-4\"/><circle cx=\"4\" cy=\"20\" r=\"2\"/>",
    "armchair": "<path d=\"M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3\"/><path d=\"M3 16a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V11a2 2 0 0 0-4 0z\"/><path d=\"M5 18v2\"/><path d=\"M19 18v2\"/>",
    "award": "<path d=\"m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526\"/><circle cx=\"12\" cy=\"8\" r=\"6\"/>",
    "bed-double": "<path d=\"M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8\"/><path d=\"M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4\"/><path d=\"M12 4v6\"/><path d=\"M2 18h20\"/>",
    "book-open": "<path d=\"M12 5v16\"/><path d=\"M20.001 19A2 2 0 0022 17V5a2 2 0 00-1.999-2L16 3.002A5 5 0 0012 5a5 5 0 00-4-2H4a2 2 0 00-2 2v12a2 2 0 001.999 2H8a5 5 0 014 2 5 5 0 014-2z\"/>",
    "briefcase": "<path d=\"M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16\"/><rect width=\"20\" height=\"14\" x=\"2\" y=\"6\" rx=\"2\"/>",
    "clipboard-check": "<rect width=\"8\" height=\"4\" x=\"8\" y=\"2\" rx=\"1\" ry=\"1\"/><path d=\"M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2\"/><path d=\"m9 14 2 2 4-4\"/>",
    "compass": "<circle cx=\"12\" cy=\"12\" r=\"10\"/><path d=\"m16.24 7.76-1.804 5.411a2 2 0 0 1-1.265 1.265L7.76 16.24l1.804-5.411a2 2 0 0 1 1.265-1.265z\"/>",
    "croissant": "<path d=\"M10.2 18H4.774a1.5 1.5 0 0 1-1.352-.97 11 11 0 0 1 .132-6.487\"/><path d=\"M18 10.2V4.774a1.5 1.5 0 0 0-.97-1.352 11 11 0 0 0-6.486.132\"/><path d=\"M18 5a4 3 0 0 1 4 3 2 2 0 0 1-2 2 10 10 0 0 0-5.139 1.42\"/><path d=\"M5 18a3 4 0 0 0 3 4 2 2 0 0 0 2-2 10 10 0 0 1 1.42-5.14\"/><path d=\"M8.709 2.554a10 10 0 0 0-6.155 6.155 1.5 1.5 0 0 0 .676 1.626l9.807 5.42a2 2 0 0 0 2.718-2.718l-5.42-9.807a1.5 1.5 0 0 0-1.626-.676\"/>",
    "door-open": "<path d=\"M10 21H2\"/><path d=\"M10 3H7a2 2 0 00-2 2v16\"/><path d=\"M14 12h.01\"/><path d=\"M19 21V5a2 2 0 00-1.675-1.974l-6.163-1.013A1 1 0 0010 3v18a1 1 0 001.124.992z\"/><path d=\"M22 21h-3\"/>",
    "filter": "<path d=\"M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z\"/>",
    "gem": "<path d=\"M10.5 3 8 9l4 13 4-13-2.5-6\"/><path d=\"M17 3a2 2 0 0 1 1.6.8l3 4a2 2 0 0 1 .013 2.382l-7.99 10.986a2 2 0 0 1-3.247 0l-7.99-10.986A2 2 0 0 1 2.4 7.8l2.998-3.997A2 2 0 0 1 7 3z\"/><path d=\"M2 9h20\"/>",
    "gift": "<path d=\"M12 7v14\"/><path d=\"M20 11v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8\"/><path d=\"M7.5 7a1 1 0 0 1 0-5A4.8 8 0 0 1 12 7a4.8 8 0 0 1 4.5-5 1 1 0 0 1 0 5\"/><rect x=\"3\" y=\"7\" width=\"18\" height=\"4\" rx=\"1\"/>",
    "graduation-cap": "<path d=\"M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z\"/><path d=\"M22 10v6\"/><path d=\"M6 12.5V16a6 3 0 0 0 12 0v-3.5\"/>",
    "handshake": "<path d=\"m11 17 2 2a1 1 0 1 0 3-3\"/><path d=\"m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4\"/><path d=\"m21 3 1 11h-2\"/><path d=\"M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3\"/><path d=\"M3 4h8\"/>",
    "house": "<path d=\"M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8\"/><path d=\"M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z\"/>",
    "layers": "<path d=\"M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z\"/><path d=\"M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12\"/><path d=\"M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17\"/>",
    "map-pin": "<path d=\"M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0\"/><circle cx=\"12\" cy=\"10\" r=\"3\"/>",
    "milk": "<path d=\"M8 2h8\"/><path d=\"M9 2v2.789a4 4 0 0 1-.672 2.219l-.656.984A4 4 0 0 0 7 10.212V20a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-9.789a4 4 0 0 0-.672-2.219l-.656-.984A4 4 0 0 1 15 4.788V2\"/><path d=\"M7 15a6.472 6.472 0 0 1 5 0 6.47 6.47 0 0 0 5 0\"/>",
    "package": "<path d=\"M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z\"/><path d=\"M12 22V12\"/><polyline points=\"3.29 7 12 12 20.71 7\"/><path d=\"m7.5 4.27 9 5.15\"/>",
    "palette": "<path d=\"M12 22a1 1 0 0 1 0-20 10 9 0 0 1 10 9 5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z\"/><circle cx=\"13.5\" cy=\"6.5\" r=\".5\" fill=\"currentColor\"/><circle cx=\"17.5\" cy=\"10.5\" r=\".5\" fill=\"currentColor\"/><circle cx=\"6.5\" cy=\"12.5\" r=\".5\" fill=\"currentColor\"/><circle cx=\"8.5\" cy=\"7.5\" r=\".5\" fill=\"currentColor\"/>",
    "refresh-cw": "<path d=\"M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8\"/><path d=\"M21 3v5h-5\"/><path d=\"M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16\"/><path d=\"M8 16H3v5\"/>",
    "shuffle": "<path d=\"m18 14 4 4-4 4\"/><path d=\"m18 2 4 4-4 4\"/><path d=\"M2 18h1.973a4 4 0 0 0 3.3-1.7l5.454-8.6a4 4 0 0 1 3.3-1.7H22\"/><path d=\"M2 6h1.972a4 4 0 0 1 3.6 2.2\"/><path d=\"M22 18h-6.041a4 4 0 0 1-3.3-1.8l-.359-.45\"/>",
    "smile": "<path d=\"M15 10V9\"/><path d=\"M16.472 15a6 6 0 01-8.943 0\"/><path d=\"M9 10V9\"/><circle cx=\"12\" cy=\"12\" r=\"10\"/>",
    "store": "<path d=\"M15 21v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5\"/><path d=\"M17.774 10.31a1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.451 0 1.12 1.12 0 0 0-1.548 0 2.5 2.5 0 0 1-3.452 0 1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.77-3.248l2.889-4.184A2 2 0 0 1 7 2h10a2 2 0 0 1 1.653.873l2.895 4.192a2.5 2.5 0 0 1-3.774 3.244\"/><path d=\"M4 10.95V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8.05\"/>",
    "timer": "<line x1=\"10\" x2=\"14\" y1=\"2\" y2=\"2\"/><line x1=\"12\" x2=\"15\" y1=\"14\" y2=\"11\"/><circle cx=\"12\" cy=\"14\" r=\"8\"/>",
    "tree-palm": "<path d=\"M13 8c0-2.76-2.46-5-5.5-5S2 5.24 2 8h2l1-1 1 1h4\"/><path d=\"M13 7.14A5.82 5.82 0 0 1 16.5 6c3.04 0 5.5 2.24 5.5 5h-3l-1-1-1 1h-3\"/><path d=\"M5.89 9.71c-2.15 2.15-2.3 5.47-.35 7.43l4.24-4.25.7-.7.71-.71 2.12-2.12c-1.95-1.96-5.27-1.8-7.42.35\"/><path d=\"M11 15.5c.5 2.5-.17 4.5-1 6.5h4c2-5.5-.5-12-1-14\"/>",
    "user": "<path d=\"M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2\"/><circle cx=\"12\" cy=\"7\" r=\"4\"/>",
    "users": "<path d=\"M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2\"/><path d=\"M16 3.128a4 4 0 0 1 0 7.744\"/><path d=\"M22 21v-2a4 4 0 0 0-3-3.87\"/><circle cx=\"9\" cy=\"7\" r=\"4\"/>",
    "utensils-crossed": "<path d=\"m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8\"/><path d=\"M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7\"/><path d=\"m2.1 21.8 6.4-6.3\"/><path d=\"m19 5-7 7\"/>",
    "wallet": "<path d=\"M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1\"/><path d=\"M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4\"/>",
    "wrench": "<path d=\"M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.106-3.105c.32-.322.863-.22.983.218a6 6 0 0 1-8.259 7.057l-7.91 7.91a1 1 0 0 1-2.999-3l7.91-7.91a6 6 0 0 1 7.057-8.259c.438.12.54.662.219.984z\"/>",
    "zap": "<path d=\"M15.914 4a1.5 1.5 0 00-2.474-1.561l-9 9A1.5 1.5 0 005.5 14h4.002a.5.5 0 01.471.666L8.086 20a1.5 1.5 0 002.475 1.56l9-9A1.5 1.5 0 0018.5 10h-3.997a.5.5 0 01-.472-.667z\"/>",
    "crown": "<path d=\"M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z\"/><path d=\"M5 21h14\"/>",
    "cog": "<path d=\"M11 10.27 7 3.34\"/><path d=\"m11 13.73-4 6.93\"/><path d=\"M12 22v-2\"/><path d=\"M12 2v2\"/><path d=\"M14 12h8\"/><path d=\"m17 20.66-1-1.73\"/><path d=\"m17 3.34-1 1.73\"/><path d=\"M2 12h2\"/><path d=\"m20.66 17-1.73-1\"/><path d=\"m20.66 7-1.73 1\"/><path d=\"m3.34 17 1.73-1\"/><path d=\"m3.34 7 1.73 1\"/><circle cx=\"12\" cy=\"12\" r=\"2\"/><circle cx=\"12\" cy=\"12\" r=\"8\"/>",
    "droplet": "<path d=\"M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z\"/>",
    "feather": "<path d=\"M14.086 18.412A2 2 0 0112.67 19H5v-7.672a2 2 0 01.586-1.414L11.75 3.75a6 6 0 118.49 8.49z\"/><path d=\"M16 8 2 22\"/><path d=\"M17.488 15H9\"/>",
  };
  const icon = (name, cls = "h-4 w-4", stroke = 2) =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${stroke}" stroke-linecap="round" stroke-linejoin="round" class="${cls}" aria-hidden="true" focusable="false">${ICONS[name] || ""}</svg>`;

  /* ================================================================== journeys.ts */

  const whoQuestion = {
    id: "for",
    topic: "Who it's for",
    title: "What best describes you?",
    sub: "About a minute, one question at a time.",
    options: [
      { v: "home", label: "Coffee at home", hint: "For me and my household" },
      { v: "office", label: "An office", hint: "Coffee for a team" },
      { v: "hotel", label: "A hotel", hint: "Coffee across a property" },
      { v: "cafe", label: "A café or restaurant", hint: "Coffee for customers" },
      { v: "event", label: "An event", hint: "A coffee bar for a day" },
    ],
  };

  const one = (a, id) => (typeof a[id] === "string" ? a[id] : undefined);
  const many = (a, id) => (Array.isArray(a[id]) ? a[id] : typeof a[id] === "string" && a[id] ? [a[id]] : []);

  const journeys = {
    home: {
      name: "Home",
      questions: [
        {
          id: "brewer",
          topic: "Your brewer",
          title: "How do you make coffee at home?",
          options: [
            { v: "espresso", label: "Espresso machine" },
            { v: "bean-to-cup", label: "Bean-to-cup machine" },
            { v: "filter", label: "Pour-over, AeroPress or French press" },
            { v: "nothing", label: "Nothing yet, I'd like to start" },
          ],
        },
        {
          id: "grind",
          topic: "Grinding",
          title: "Do you grind your own beans?",
          when: (a) => one(a, "brewer") === "espresso" || one(a, "brewer") === "filter",
          options: [
            { v: "yes", label: "Yes, I grind fresh" },
            { v: "no", label: "No, I need it ground" },
          ],
        },
        {
          id: "start",
          topic: "How you start",
          title: "How would you like to start?",
          when: (a) => one(a, "brewer") === "nothing",
          options: [
            { v: "simplest", label: "The simplest way, no equipment" },
            { v: "pour-over", label: "A simple pour-over set" },
            { v: "machine", label: "I'm thinking about a machine" },
          ],
        },
        {
          id: "serve",
          topic: "Black or milk",
          title: "How do you usually take it?",
          options: [
            { v: "black", label: "Black" },
            { v: "milk", label: "With milk" },
            { v: "both", label: "Both, depending on the day" },
          ],
        },
        {
          id: "taste",
          topic: "Taste",
          title: "Which cup sounds best?",
          options: [
            { v: "chocolate", label: "Chocolate, nuts and caramel" },
            { v: "fruit", label: "Bright fruit and berries" },
            { v: "floral", label: "Floral and delicate, like tea" },
            { v: "bold", label: "Bold and intense" },
          ],
        },
        {
          id: "after",
          topic: "What you're after",
          title: "What are you looking for?",
          options: [
            { v: "everyday", label: "A reliable everyday favourite" },
            { v: "new", label: "Something new each time" },
            { v: "rare", label: "The rarest coffee you can find" },
            { v: "gift", label: "A gift" },
          ],
        },
        {
          id: "people",
          topic: "Who's drinking",
          title: "Who's drinking it?",
          options: [
            { v: "me", label: "Just me" },
            { v: "two", label: "Two of us" },
            { v: "house", label: "A full house" },
          ],
        },
      ],
    },

    office: {
      name: "Office",
      questions: [
        {
          id: "setup",
          topic: "Your setup",
          title: "What's in your office today?",
          options: [
            { v: "nothing", label: "Nothing yet" },
            { v: "capsules", label: "Capsules or pods" },
            { v: "bean-to-cup", label: "An automatic bean-to-cup machine" },
            { v: "espresso", label: "An espresso machine with someone to run it" },
          ],
        },
        {
          id: "drinks",
          topic: "What people drink",
          title: "What do people mostly drink?",
          options: [
            { v: "milk", label: "Lattes and flat whites" },
            { v: "black", label: "Espresso and Americano" },
            { v: "mix", label: "A real mix" },
            { v: "filter", label: "We also want filter for meetings and guests" },
          ],
        },
        {
          id: "identity",
          topic: "Your coffee",
          title: "What should your coffee say about you?",
          options: [
            { v: "crowd", label: "A crowd-pleaser everyone enjoys" },
            { v: "character", label: "Something with character" },
            { v: "impress", label: "Impressive enough for clients" },
          ],
        },
        {
          id: "size",
          topic: "Team size",
          title: "How many people drink coffee each day?",
          options: [
            { v: "under-20", label: "Under 20" },
            { v: "20-50", label: "20 to 50" },
            { v: "50-150", label: "50 to 150" },
            { v: "150-plus", label: "Over 150" },
          ],
        },
        {
          id: "priority",
          topic: "What matters",
          title: "What matters most to you?",
          options: [
            { v: "speed", label: "Speed at the morning rush" },
            { v: "taste", label: "Taste and impressing visitors" },
            { v: "upkeep", label: "Easy upkeep" },
            { v: "cost", label: "Predictable monthly cost" },
          ],
        },
      ],
    },

    hotel: {
      name: "Hotel",
      questions: [
        {
          id: "stage",
          topic: "Your property",
          title: "Where is your property today?",
          options: [
            { v: "opening", label: "Opening a new property" },
            { v: "switching", label: "Switching from our current roaster" },
            { v: "outlet", label: "Adding or refreshing an outlet" },
            { v: "reviewing", label: "Reviewing what we have" },
          ],
        },
        {
          id: "outlets",
          topic: "Outlets",
          title: "Where do guests drink coffee?",
          sub: "Pick all that apply.",
          multi: true,
          options: [
            { v: "breakfast", label: "Breakfast and buffet" },
            { v: "lobby", label: "Lobby or café outlet" },
            { v: "in-room", label: "In-room" },
            { v: "restaurant", label: "Restaurant, bar and events" },
          ],
        },
        {
          id: "experience",
          topic: "Guest experience",
          title: "How would you describe your guests' experience?",
          options: [
            { v: "business", label: "Efficient business stay" },
            { v: "luxury", label: "Signature luxury" },
            { v: "boutique", label: "Lifestyle and boutique" },
            { v: "resort", label: "Family resort" },
          ],
        },
        {
          id: "rooms",
          topic: "Rooms",
          title: "How many rooms?",
          options: [
            { v: "under-100", label: "Under 100" },
            { v: "100-250", label: "100 to 250" },
            { v: "250-500", label: "250 to 500" },
            { v: "500-plus", label: "Over 500" },
          ],
        },
        {
          id: "priority",
          topic: "What matters",
          title: "What matters most?",
          options: [
            { v: "consistency", label: "Consistency across every outlet" },
            { v: "story", label: "A coffee story guests talk about" },
            { v: "training", label: "Training for changing teams" },
            { v: "speed", label: "Speed at the breakfast peak" },
          ],
        },
      ],
    },

    cafe: {
      name: "Café",
      questions: [
        {
          id: "stage",
          topic: "Where you are",
          title: "Where are you today?",
          options: [
            { v: "opening", label: "Planning to open" },
            { v: "reviewing", label: "Running, and reviewing our roaster" },
            { v: "restaurant", label: "Adding specialty to a restaurant menu" },
            { v: "several", label: "Several sites" },
          ],
        },
        {
          id: "bar",
          topic: "Your bar",
          title: "What's on your bar?",
          options: [
            { v: "nothing", label: "Nothing yet" },
            { v: "espresso", label: "Espresso machine and grinder" },
            { v: "espresso-filter", label: "Espresso plus a filter bar" },
            { v: "bean-to-cup", label: "Bean-to-cup for quick service" },
          ],
        },
        {
          id: "orders",
          topic: "What customers order",
          title: "What do your customers order most?",
          options: [
            { v: "milk", label: "Milk drinks" },
            { v: "black", label: "Espresso and black" },
            { v: "filter", label: "Filter and specialty" },
            { v: "mix", label: "A balanced mix" },
          ],
        },
        {
          id: "known",
          topic: "House coffee",
          title: "What should your house coffee be known for?",
          options: [
            { v: "classic", label: "Classic, chocolatey comfort" },
            { v: "fruity", label: "Bright, fruity specialty" },
            { v: "bold", label: "Bold and strong" },
            { v: "rotating", label: "A rotating guest offer" },
          ],
        },
        {
          id: "kilos",
          topic: "Weekly volume",
          title: "How much coffee do you use a week?",
          options: [
            { v: "under-5", label: "Under 5 kg" },
            { v: "5-15", label: "5 to 15 kg" },
            { v: "15-30", label: "15 to 30 kg" },
            { v: "30-plus", label: "Over 30 kg" },
          ],
        },
      ],
    },
  };

  /** The questions this visitor will be asked, in order (Question 1 included unless they arrived from a Solutions page). */
  function questionsFor(journey, a, skipWho) {
    const list = journey ? journeys[journey].questions.filter((q) => !q.when || q.when(a)) : [];
    return skipWho ? list : [whoQuestion, ...list];
  }

  const labelOf = (q, v) =>
    (Array.isArray(v) ? v : v ? [v] : [])
      .map((x) => {
        const o = q.options.find((o) => o.v === x);
        return o ? o.label : x;
      })
      .join(", ");

  /* ---------- Scoring ---------- */

  const WHEEL = ["bold", "chocolate", "fruit", "floral"];
  const distance = (a, b) => Math.abs(WHEEL.indexOf(a) - WHEEL.indexOf(b));

  const has = (c, tag) => !!(c.bestFor && c.bestFor.includes(tag));
  const is = (c, ...cs) => cs.includes(c.composition);
  const isEspresso = (c) => c.methods.includes("espresso");
  const isFilter = (c) => c.methods.includes("filter");
  const minPrice = (c) => Math.min(...c.packs.map((p) => p.price));

  function rank(catalogue, r, available) {
    let pool = catalogue.filter((c) => r.pool(c) && available(c));
    if (r.milk === "only") {
      const milky = pool.filter((c) => c.milkFriendly);
      if (milky.length) pool = milky;
    }
    const lead = r.lead || [];
    const order = (c) => (lead.includes(c.slug) ? lead.indexOf(c.slug) : lead.length);
    return pool
      .map((coffee) => {
        let score = 0;
        if (r.flavour && coffee.flavour) score += coffee.flavour === r.flavour ? 3 : distance(coffee.flavour, r.flavour) === 1 ? 1 : 0;
        if (r.milk === "favour" && coffee.milkFriendly) score += 1;
        score += r.boost ? r.boost(coffee) : 0;
        return { coffee, score };
      })
      .sort((x, y) => y.score - x.score || order(x.coffee) - order(y.coffee) || minPrice(x.coffee) - minPrice(y.coffee));
  }

  /** Best coffee from a different flavour family, nudged towards families further away on the wheel. */
  function alternativeTo(top, ranked, exclude = []) {
    const f = top.coffee.flavour;
    const best = ranked
      .filter((r) => r.coffee.slug !== top.coffee.slug && !exclude.includes(r.coffee.slug) && (!f || !r.coffee.flavour || r.coffee.flavour !== f))
      .map((r) => ({ r, s: r.score + (f && r.coffee.flavour && distance(f, r.coffee.flavour) >= 2 ? 1 : 0) }))
      .sort((a, b) => b.s - a.s)[0];
    return best ? best.r : undefined;
  }

  /* ---------- Results ---------- */

  const ROASTER = "Roasted in the UAE by Ajeyudu Pathuri, Middle East Coffee Roasting Champion 2023.";

  /**
   * Draft planning rules, to be approved by the client:
   * office cups a month = people (midpoint) x 2 cups a working day x 22 days;
   * café 1 kg bags a week = the middle of the chosen band.
   */
  const VOLUME = {
    officePeople: { "under-20": 15, "20-50": 35, "50-150": 100, "150-plus": 180 },
    cupsPerPersonPerDay: 2,
    workingDays: 22,
    cafeKilos: { "under-5": 3, "5-15": 10, "15-30": 22, "30-plus": 35 },
  };

  const fmt = (n) => n.toLocaleString("en-US");
  const notes3 = (c) => c.notes.slice(0, 3).join(", ");

  function match(journey, a, catalogue, available) {
    switch (journey) {
      case "home":
        return homeMatch(a, catalogue, available);
      case "office":
        return officeMatch(a, catalogue, available);
      case "hotel":
        return hotelMatch(a, catalogue, available);
      case "cafe":
        return cafeMatch(a, catalogue, available);
    }
    return undefined;
  }

  const tasteWords = {
    chocolate: "chocolate, nuts and caramel",
    fruit: "bright fruit",
    floral: "floral, delicate cups",
    bold: "bold, intense coffee",
  };

  function homeMatch(a, catalogue, available) {
    const brewer = one(a, "brewer");
    const start = one(a, "start");
    const serve = one(a, "serve");
    const after = one(a, "after");
    const taste = one(a, "taste") || "chocolate";
    const espresso = brewer === "espresso" || brewer === "bean-to-cup" || (brewer === "nothing" && start === "machine");
    // There is no bold filter coffee: bold filter answers fall back to chocolate and caramel.
    const flavour = !espresso && taste === "bold" ? "chocolate" : taste;

    const boost = (c) => {
      switch (after) {
        case "everyday":
          return (is(c, "Signature Espresso", "Classic Espresso") ? 2 : 0) + (has(c, "everyday") ? 1 : 0);
        case "new":
          return (is(c, "Single Origin Espresso", "Filter Coffee") ? 2 : 0) + (has(c, "explorers") ? 1 : 0);
        case "rare":
          return (is(c, "Limited Micro-Lot") ? 4 : 0) + (has(c, "guest-filter") ? 1 : 0);
        case "gift":
          return (has(c, "gifts") ? 3 : 0) + (is(c, "Signature Espresso") ? 1 : 0);
        default:
          return 0;
      }
    };
    const lead =
      serve === "milk"
        ? ["milestone", "magnifico", "pulse", "meridian", "maestro"]
        : ["pulse", "milestone", "magnifico", "meridian", "maestro", "kenya-gondo-aa", "ethiopia-guji", "kolliberri", "embrujo-shaman", "las-palmas"];
    const ranked = rank(catalogue, { pool: espresso ? isEspresso : isFilter, milk: serve === "milk" ? "only" : serve === "both" ? "favour" : undefined, flavour, boost, lead }, available);
    const top = ranked[0];
    const alt = top ? alternativeTo(top, ranked) : undefined;

    const brewerWords = {
      espresso: "espresso machine",
      "bean-to-cup": "bean-to-cup machine",
      filter: "pour-over mornings",
      nothing: start === "machine" ? "first espresso machine" : "first brew at home",
    };
    const headline = after === "gift" ? `A gift for someone who loves ${tasteWords[taste]}.` : `For your ${brewerWords[brewer || "filter"]} and a love of ${tasteWords[taste]}.`;

    const why = (c) => {
      const out = [];
      out.push(c.flavour === flavour ? `${notes3(c)}: right in the ${tasteWords[taste]} you chose.` : `${notes3(c)}: close to the ${tasteWords[taste]} you chose.`);
      out.push(espresso ? `Roasted for espresso, so it suits your ${brewer === "bean-to-cup" ? "bean-to-cup machine" : "machine"}.` : "Roasted for filter brewing, so it shines in a pour-over.");
      if (serve === "milk") out.push(c.milkFriendly ? "Holds its flavour through milk." : "Best enjoyed black.");
      else if (after === "rare") out.push(is(c, "Limited Micro-Lot") ? "A limited micro-lot, so it won't be around for long." : "A distinctive lot when you want something special.");
      else if (after === "new") out.push(is(c, "Single Origin Espresso", "Filter Coffee") ? "A single origin with a story worth tasting." : "A good base to explore from.");
      else if (after === "gift") out.push("A coffee that feels like a present.");
      else out.push(is(c, "Signature Espresso", "Classic Espresso") ? "One of our signature everyday coffees." : "Easy to enjoy every day.");
      return out;
    };

    const notes = [];
    let equipment;
    if (brewer === "nothing") {
      equipment =
        start === "machine"
          ? { title: "Thinking about a machine", body: "We don't sell home machines yet. Start with this coffee now, and leave your email for a note when home machines arrive.", label: "Coming later" }
          : start === "pour-over"
            ? { title: "A simple pour-over set", body: "A dripper, paper filters and a kettle are all you need. The recipe below walks you through your first cup.", label: "Easy start" }
            : { title: "The simplest way to start", body: "No equipment needed. Easy formats for home are being confirmed; leave your email and we'll tell you when they're ready.", label: "Easy start" };
    }
    if (one(a, "grind") === "no") notes.push("Our coffees are sold as whole beans for now. Ground options are being confirmed for each coffee.");
    if (after === "gift") notes.push("You can send it straight to someone else: add their address at checkout.");
    if (top && is(top.coffee, "Classic Espresso")) notes.push("Our Classics blend Arabica with Robusta, built for strength, body and crema.");

    const people = one(a, "people");
    const afterOpt = journeys.home.questions[5].options.find((o) => o.v === after);
    return {
      journey: "home",
      headline,
      summary: [brewerWords[brewer || "filter"], tasteWords[taste], (afterOpt ? afterOpt.label : "").toLowerCase()].filter(Boolean),
      primary: top && { slug: top.coffee.slug, role: "Your closest match", why: why(top.coffee) },
      alternative: alt && { slug: alt.coffee.slug, role: "Something different", why: why(alt.coffee).slice(0, 2) },
      equipment,
      brew: brewer === "espresso" || (brewer === "nothing" && start === "machine") ? "espresso" : brewer === "bean-to-cup" ? "bean-to-cup" : "pour-over",
      size: people === "house" ? "1kg" : "250g",
      notes,
      priority: false,
      offer: {
        title: "Get this by email",
        body: "To get this by email, fill in your name and email. We'll send you your match, your answers from every step and a step-by-step brew recipe for your setup, and invite you to our tastings.",
        button: "Email me my result",
      },
    };
  }

  function officeMatch(a, catalogue, available) {
    const setup = one(a, "setup");
    const drinks = one(a, "drinks");
    const identity = one(a, "identity");
    const size = one(a, "size") || "20-50";
    const priority = one(a, "priority");

    const boost = (c) =>
      identity === "crowd"
        ? (is(c, "Classic Espresso") ? 3 : 0) + (is(c, "Signature Espresso") ? 1 : 0) + (has(c, "offices") ? 1 : 0)
        : identity === "character"
          ? (is(c, "Signature Espresso") ? 3 : 0) + (is(c, "Single Origin Espresso") ? 1 : 0)
          : (is(c, "Signature Espresso") ? 3 : 0) + (has(c, "offices") ? 1 : 0);
    const lead = drinks === "milk" ? ["milestone", "magnifico", "pulse", "maestro", "meridian"] : ["pulse", "milestone", "magnifico", "maestro", "meridian"];
    const ranked = rank(catalogue, { pool: isEspresso, milk: drinks === "milk" ? "only" : drinks === "mix" ? "favour" : undefined, boost, lead }, available);
    const top = ranked[0];

    // A filter coffee for meetings, or a micro-lot when the coffee must impress clients.
    let partner;
    if (drinks === "filter" || identity === "impress") {
      const filters = rank(
        catalogue,
        { pool: isFilter, boost: (c) => (identity === "impress" ? (is(c, "Limited Micro-Lot") ? 4 : 0) : 0) + (has(c, "meetings") ? 3 : 0) + (has(c, "everyday") ? 1 : 0), lead: ["kolliberri", "ponderosa-geisha"] },
        available,
      );
      const f = filters[0] && filters[0].coffee;
      if (f)
        partner = {
          slug: f.slug,
          role: identity === "impress" && is(f, "Limited Micro-Lot") ? "For client meetings" : "Filter for meetings and guests",
          why: [is(f, "Limited Micro-Lot") ? "A limited micro-lot that turns a meeting coffee into a talking point." : `${notes3(f)}. Easy to brew in a batch for meetings.`],
        };
    }
    const alt = top ? alternativeTo(top, ranked) : undefined;

    const people = VOLUME.officePeople[size];
    const cups = people * VOLUME.cupsPerPersonPerDay * VOLUME.workingDays;
    const sizeLabel = labelOf(journeys.office.questions[3], size).toLowerCase();

    const tiers = ["Everyday automatic", "Professional", "Smart and flagship"];
    let tier = size === "under-20" ? 0 : size === "20-50" ? (priority === "speed" ? 1 : 0) : size === "50-150" ? (priority === "taste" ? 2 : 1) : 2;
    if (priority === "taste" && tier === 0) tier = 1;
    const tierBody = [
      "A reliable bean-to-cup machine that makes espresso, Americano and milk drinks at the touch of a button.",
      "A higher-capacity bean-to-cup with fresh milk, built for a busy morning rush.",
      "Our most capable automatic machines, with fresh milk and the speed for large teams and client areas.",
    ][tier];
    const equipment =
      setup === "nothing" || setup === "capsules"
        ? { title: tiers[tier], body: `${tierBody} Final model confirmed in your plan.`, label: "Equipment" }
        : setup === "bean-to-cup"
          ? { title: "Your bean-to-cup machine", body: "We calibrate your machine to the new coffee and train whoever looks after it.", label: "Setup" }
          : setup === "espresso"
            ? { title: "Your espresso machine", body: "We dial in the coffee on your machine and train the people who run it.", label: "Setup" }
            : undefined;

    const drinkWords = { milk: "lattes and flat whites", black: "espresso and Americano", mix: "a real mix", filter: "espresso and filter" };
    const why = (c) => [
      `${notes3(c)}.`,
      drinks === "milk" ? (c.milkFriendly ? "Holds its flavour through milk, for the lattes your team drinks." : "Best black.") : "Works as espresso and as a long Americano.",
      is(c, "Classic Espresso") ? "Arabica and Robusta, built for strength and volume." : identity === "impress" ? "A signature blend you'll be proud to serve to clients." : "A signature blend with real character.",
    ];

    return {
      journey: "office",
      headline: `For a team of ${sizeLabel} who drink ${drinkWords[drinks || "mix"]}.`,
      summary: [sizeLabel + " people", drinkWords[drinks || "mix"], labelOf(journeys.office.questions[0], setup).toLowerCase()],
      primary: top && { slug: top.coffee.slug, role: "Your office coffee", why: why(top.coffee) },
      partner,
      alternative: alt && { slug: alt.coffee.slug, role: "Something different", why: why(alt.coffee).slice(0, 2) },
      equipment,
      volume: `About ${fmt(cups)} cups a month for a team of ${sizeLabel}.`,
      brew: setup === "espresso" ? "espresso" : "bean-to-cup",
      size: "1kg",
      notes: [],
      priority: size === "50-150" || size === "150-plus" || setup === "capsules",
      offer: {
        title: "Get your office plan by email",
        body: "To get this by email, fill in your name and email. We'll send your answers with a tailored plan: coffee, machine, cups per day and service, sized for your team. We'll also arrange an office tasting.",
        button: "Email me the plan",
      },
    };
  }

  const outletCards = {
    breakfast: { title: "Breakfast and buffet", body: "Your house espresso on fast, consistent equipment for the breakfast peak." },
    lobby: { title: "Lobby or café outlet", body: "House espresso and the signature filter, served by a trained team." },
    "in-room": { title: "In-room", body: "Capsules or drip bags for compatible in-room setups, confirmed in your plan." },
    restaurant: { title: "Restaurant, bar and events", body: "Espresso to finish the meal, and a signature serve for special occasions." },
  };

  function hotelMatch(a, catalogue, available) {
    const stage = one(a, "stage");
    const outlets = many(a, "outlets");
    const experience = one(a, "experience");
    const rooms = one(a, "rooms");
    const priority = one(a, "priority");

    const boost = (c) =>
      (is(c, "Signature Espresso") ? 2 : 0) +
      (has(c, "hotels") ? 1 : 0) +
      (experience === "business" || experience === "resort" ? (is(c, "Classic Espresso") ? 1 : 0) + (has(c, "high-volume") ? 1 : 0) : 0) +
      (experience === "boutique" ? (has(c, "explorers") ? 2 : 0) : 0);
    const espresso = rank(catalogue, { pool: isEspresso, milk: outlets.includes("breakfast") ? "favour" : undefined, boost, lead: ["meridian", "milestone", "pulse", "magnifico"] }, available);
    const filters = rank(
      catalogue,
      {
        pool: isFilter,
        boost: (c) =>
          experience === "luxury" || priority === "story"
            ? has(c, "signature-serve")
              ? 4
              : 0
            : experience === "boutique"
              ? (has(c, "explorers") ? 3 : 0) + (has(c, "guest-filter") ? 1 : 0)
              : (has(c, "everyday") ? 3 : 0) + (has(c, "meetings") ? 1 : 0),
        lead: ["kolliberri", "ponderosa-geisha", "kenya-gondo-aa", "las-palmas"],
      },
      available,
    );
    const top = espresso[0];
    const f = filters[0];
    const alt = top ? alternativeTo(top, espresso) : undefined;

    const expWords = { business: "an efficient business stay", luxury: "a signature luxury stay", boutique: "a lifestyle and boutique stay", resort: "a family resort" };
    const proof = {
      consistency: "One house espresso, calibrated by our team in every outlet.",
      story: ROASTER,
      training: "Training for every outlet, repeated as your team changes.",
      speed: "Blends built to hold up through the breakfast peak.",
    };

    return {
      journey: "hotel",
      headline: `For ${expWords[experience || "business"]}, across ${outlets.length || 1} ${outlets.length === 1 ? "outlet" : "outlets"}.`,
      summary: [expWords[experience || "business"], `${outlets.length || 1} outlets`, labelOf(journeys.hotel.questions[3], rooms).toLowerCase() + " rooms"],
      primary: top && {
        slug: top.coffee.slug,
        role: "House espresso",
        why: [`${notes3(top.coffee)}.`, top.coffee.milkFriendly ? "Holds its flavour in the milk drinks guests order at breakfast." : "Clean and expressive as espresso.", proof[priority || "consistency"]],
      },
      partner: f && {
        slug: f.coffee.slug,
        role: "Signature filter",
        why: [is(f.coffee, "Limited Micro-Lot") ? "A limited micro-lot for a signature serve guests remember." : `${notes3(f.coffee)}, brewed for the lounge and meetings.`],
      },
      alternative: alt && { slug: alt.coffee.slug, role: "Something different", why: [`${notes3(alt.coffee)}.`] },
      outlets: (outlets.length ? outlets : ["lobby"]).map((o) => outletCards[o]).filter(Boolean),
      brew: "espresso",
      size: "1kg",
      notes: [],
      priority: stage === "opening" || stage === "switching",
      offer: {
        title: "Get your property plan by email",
        body: "To get this by email, fill in your name and email. We'll send your answers with a plan by outlet, a guest signature serve and a training plan. We'll also arrange a tasting at your property.",
        button: "Email me the plan",
      },
    };
  }

  function cafeMatch(a, catalogue, available) {
    const stage = one(a, "stage");
    const bar = one(a, "bar");
    const orders = one(a, "orders");
    const known = one(a, "known") || "classic";
    const kilos = one(a, "kilos") || "5-15";

    const flavour = known === "fruity" ? "fruit" : known === "bold" ? "bold" : "chocolate";
    const espresso = rank(
      catalogue,
      {
        pool: isEspresso,
        milk: orders === "milk" ? "only" : orders === "mix" ? "favour" : undefined,
        flavour,
        boost: (c) => (has(c, "cafes") ? 1 : 0) + (known === "fruity" && has(c, "explorers") ? 2 : 0) + (known === "rotating" && is(c, "Signature Espresso") ? 1 : 0),
        lead: orders === "milk" ? ["milestone", "pulse", "magnifico"] : ["pulse", "milestone", "magnifico"],
      },
      available,
    );
    const guest = rank(
      catalogue,
      {
        pool: isFilter,
        flavour: known === "fruity" ? "fruit" : known === "rotating" ? undefined : "chocolate",
        boost: (c) => (known === "rotating" ? (is(c, "Limited Micro-Lot") ? 4 : 0) : 0) + (has(c, "guest-filter") ? 2 : 0) + (has(c, "explorers") ? 1 : 0),
        lead: ["las-palmas", "kenya-gondo-aa", "ponderosa-geisha"],
      },
      available,
    );
    const top = espresso[0];
    const g = guest[0];
    const alt = top ? alternativeTo(top, espresso) : undefined;

    const bags = VOLUME.cafeKilos[kilos];
    const tier = kilos === "under-5" ? 0 : kilos === "5-15" ? 1 : 2;
    const equipment =
      bar === "nothing"
        ? {
            title: ["Compact espresso setup", "Two-group espresso bar", "Multi-group bar with dedicated grinders"][tier],
            body: "Espresso machine, on-demand grinders and water treatment, sized to your volume and installed by our technicians. Final models confirmed in your plan.",
            label: "Equipment",
          }
        : bar === "bean-to-cup"
          ? { title: "Bean-to-cup calibration", body: "We set up your machine for the house espresso and train the team on daily cleaning.", label: "Setup" }
          : { title: "Your bar, dialled in", body: "We calibrate your grinders and machine to the new coffees and train your baristas.", label: "Setup" };

    const knownWords = { classic: "classic, chocolatey comfort", fruity: "bright, fruity specialty", bold: "bold, strong coffee", rotating: "a rotating guest offer" };
    return {
      journey: "cafe",
      headline: `For a house coffee known for ${knownWords[known]}.`,
      summary: [knownWords[known], labelOf(journeys.cafe.questions[2], orders).toLowerCase(), labelOf(journeys.cafe.questions[4], kilos) + " a week"],
      primary: top && {
        slug: top.coffee.slug,
        role: "House espresso",
        why: [
          `${notes3(top.coffee)}.`,
          orders === "milk" ? "Cuts through milk without losing its character." : "Sweet and balanced as a straight shot.",
          is(top.coffee, "Classic Espresso") ? "Arabica and Robusta, built for strength, body and crema." : "Consistent shot after shot, even on a busy morning.",
        ],
      },
      partner: g && {
        slug: g.coffee.slug,
        role: "Guest filter",
        why: [known === "rotating" && is(g.coffee, "Limited Micro-Lot") ? "Start the rotation with a limited micro-lot, and a new menu story each season." : `${notes3(g.coffee)}, a filter worth putting on the menu.`],
      },
      alternative: alt && { slug: alt.coffee.slug, role: "Something different", why: [`${notes3(alt.coffee)}.`] },
      equipment,
      volume: `About ${bags} one-kilo bags a week, on wholesale prices from one box (six 1 kg bags) per coffee.`,
      brew: bar === "bean-to-cup" ? "bean-to-cup" : "espresso",
      size: "1kg",
      notes: [],
      priority: kilos === "15-30" || kilos === "30-plus" || stage === "several",
      offer: {
        title: "Get your menu and equipment plan by email",
        body: "To get this by email, fill in your name and email. We'll send your answers with your house espresso and guest filter, weekly volume, equipment and barista training. Then come and cup them at our roastery.",
        button: "Email me the plan",
      },
    };
  }

  /* ---------- URLs ---------- */

  /** The result link keeps every answer, so it can be shared or reopened in a meeting. */
  function toQuery(journey, a, skipWho) {
    const p = new URLSearchParams();
    if (journey) p.set("for", journey);
    if (skipWho) p.set("from", "solutions");
    for (const [k, v] of Object.entries(a)) {
      if (k === "for" || v === undefined || (Array.isArray(v) && !v.length)) continue;
      p.set(k, Array.isArray(v) ? v.join(",") : v);
    }
    return p.toString();
  }

  function fromQuery(params) {
    const get = (k) => params.get(k) || undefined;
    const who = get("for");
    const journey = ["home", "office", "hotel", "cafe", "event"].find((j) => j === who);
    const answers = {};
    if (journey && journey !== "event") {
      for (const q of journeys[journey].questions) {
        const v = get(q.id);
        if (!v) continue;
        const vals = v.split(",").filter((x) => q.options.some((o) => o.v === x));
        if (vals.length) answers[q.id] = q.multi ? vals : vals[0];
      }
    }
    return { journey, answers, skipWho: get("from") === "solutions" };
  }

  /** Every query key the journey owns (other keys, e.g. Shopify's preview_theme_id, are kept). */
  const OWN_KEYS = new Set(["for", "from", "done", "customer_posted", ...Object.values(journeys).flatMap((j) => j.questions.map((q) => q.id))]);

  /* ================================================================== brew.ts (the guides a result uses) */

  const brewGuides = {
    "pour-over": {
      name: "V60 / Pour-over",
      summary: "Clean and expressive. Lets origin character and aroma come through.",
      dose: "15 g coffee",
      water: "250 g water",
      ratio: "1:16.7",
      temp: "92 to 94 °C",
      grind: "Medium-fine, like coarse sand",
      time: "2:45 to 3:15 total",
      steps: [
        "Rinse the paper filter with hot water and discard the rinse.",
        "Add the coffee, level the bed and start a timer.",
        "Bloom: pour about 40 g of water, swirl gently and wait 30 to 40 seconds.",
        "Pour slowly in circles to 150 g by 1:00.",
        "Continue to 250 g by about 1:45, then let it draw down.",
        "Swirl once, serve and taste again as the cup cools.",
      ],
    },
    espresso: {
      name: "Espresso",
      summary: "Concentrated and textured. The base for milk drinks too.",
      dose: "18 g coffee",
      water: "36 to 40 g out",
      ratio: "1:2 to 1:2.2",
      temp: "92 to 94 °C",
      grind: "Fine; adjust to hit the time",
      time: "27 to 32 seconds",
      steps: [
        "Purge the group head and dry the basket.",
        "Dose 18 g, distribute evenly and tamp level.",
        "Start the shot straight away and aim for 36 to 40 g in the cup.",
        "Taste, then adjust grind one step at a time.",
        "For milk drinks, steam milk to 55 to 65 °C with a silky texture.",
      ],
    },
    "bean-to-cup": {
      name: "Bean-to-cup machine",
      summary: "Consistent, one-touch coffee for home or the office.",
      dose: "Machine strength: medium to strong",
      water: "Espresso program: about 40 ml",
      ratio: "Around 1:2",
      temp: "High temperature setting",
      grind: "One or two steps finer than factory default",
      time: "Machine controlled",
      steps: [
        "Fill the hopper with fresh beans only as much as you will use within a week.",
        "Run one shot to clear old grounds after changing coffee.",
        "Adjust grind a step at a time while grinding, then taste after two cups.",
        "Keep the milk system and brew unit clean daily for the best taste.",
      ],
    },
    "french-press": {
      name: "French press",
      summary: "Full-bodied and forgiving. Great for sharing.",
      dose: "30 g coffee",
      water: "500 g water",
      ratio: "1:16.7",
      temp: "93 to 95 °C",
      grind: "Coarse, like sea salt",
      time: "4 minutes, then press",
      steps: [
        "Add coffee and all the water, then stir gently.",
        "Place the lid on and steep for 4 minutes.",
        "Break the crust, skim the foam and press slowly.",
        "Pour everything out straight away so it does not over-extract.",
      ],
    },
  };

  /* ================================================================== visuals.ts */
  /*
   * How each answer looks. img = key of the photo in the data island's "images" (asset URLs come
   * from Liquid), w/h = its real size, pos = CSS object-position for the tall, narrow tile.
   * icon = a Lucide icon. Answers with neither (team size, rooms, kilos) are set as large type.
   */
  const VISUALS = {
    "who.for.home": { img: "who-home", w: 1024, h: 1536, pos: "center 35%" },
    "who.for.office": { img: "who-office-2", w: 1200, h: 1800, pos: "center 35%" },
    "who.for.hotel": { img: "who-hotel", w: 1024, h: 1536, pos: "center 40%" },
    "who.for.cafe": { img: "who-cafe", w: 1200, h: 1800, pos: "center 100%" },
    "who.for.event": { img: "who-event", w: 1024, h: 1536, pos: "center 30%" },

    "home.brewer.espresso": { img: "solutions-cafes-tune", w: 1100, h: 880, pos: "center" },
    "home.brewer.bean-to-cup": { img: "office-machine-2", w: 711, h: 900, pos: "center" },
    "home.brewer.filter": { img: "solutions-home-recipe", w: 1100, h: 880, pos: "60% center" },
    "home.brewer.nothing": { img: "solutions-home-stage-dripbag", w: 1250, h: 1000, pos: "62% center" },
    "home.grind.yes": { icon: "cog" },
    "home.grind.no": { icon: "package" },
    "home.start.simplest": { icon: "feather" },
    "home.start.pour-over": { icon: "filter" },
    "home.start.machine": { icon: "zap" },
    "home.serve.black": { icon: "droplet" },
    "home.serve.milk": { icon: "milk" },
    "home.serve.both": { icon: "shuffle" },
    "home.taste.chocolate": { img: "taste2-chocolate", w: 1536, h: 1024, pos: "40% center" },
    "home.taste.fruit": { img: "taste2-fruit", w: 1536, h: 1024, pos: "45% center" },
    "home.taste.floral": { img: "taste2-floral", w: 1536, h: 1024, pos: "52% center" },
    "home.taste.bold": { img: "taste2-bold", w: 1536, h: 1024, pos: "57% center" },
    "home.after.everyday": { icon: "refresh-cw" },
    "home.after.new": { icon: "compass" },
    "home.after.rare": { icon: "gem" },
    "home.after.gift": { icon: "gift" },
    "home.people.me": { icon: "user" },
    "home.people.two": { icon: "users" },
    "home.people.house": { icon: "house" },

    "office.setup.nothing": { img: "shoot-office-plan", w: 1716, h: 916, pos: "center" },
    "office.setup.capsules": { img: "shoot-office-care", w: 1717, h: 916, pos: "45% center" },
    "office.setup.bean-to-cup": { img: "shoot-office-tune", w: 1100, h: 880, pos: "40% center" },
    "office.setup.espresso": { img: "office-tune", w: 1100, h: 880, pos: "55% center" },
    "office.drinks.milk": { icon: "milk" },
    "office.drinks.black": { icon: "droplet" },
    "office.drinks.mix": { icon: "shuffle" },
    "office.drinks.filter": { icon: "filter" },
    "office.identity.crowd": { icon: "smile" },
    "office.identity.character": { icon: "sparkles" },
    "office.identity.impress": { icon: "handshake" },
    "office.priority.speed": { icon: "timer" },
    "office.priority.taste": { icon: "award" },
    "office.priority.upkeep": { icon: "wrench" },
    "office.priority.cost": { icon: "wallet" },

    "hotel.stage.opening": { icon: "door-open" },
    "hotel.stage.switching": { icon: "refresh-cw" },
    "hotel.stage.outlet": { icon: "store" },
    "hotel.stage.reviewing": { icon: "clipboard-check" },
    "hotel.outlets.breakfast": { icon: "croissant" },
    "hotel.outlets.lobby": { icon: "armchair" },
    "hotel.outlets.in-room": { icon: "bed-double" },
    "hotel.outlets.restaurant": { icon: "utensils-crossed" },
    "hotel.experience.business": { icon: "briefcase" },
    "hotel.experience.luxury": { icon: "crown" },
    "hotel.experience.boutique": { icon: "palette" },
    "hotel.experience.resort": { icon: "tree-palm" },
    "hotel.priority.consistency": { icon: "layers" },
    "hotel.priority.story": { icon: "book-open" },
    "hotel.priority.training": { icon: "graduation-cap" },
    "hotel.priority.speed": { icon: "timer" },

    "cafe.stage.opening": { icon: "door-open" },
    "cafe.stage.reviewing": { icon: "refresh-cw" },
    "cafe.stage.restaurant": { icon: "utensils-crossed" },
    "cafe.stage.several": { icon: "map-pin" },
    "cafe.bar.nothing": { img: "solutions-cafes-install", w: 1100, h: 880, pos: "center" },
    "cafe.bar.espresso": { img: "solutions-home-grind", w: 1100, h: 880, pos: "center" },
    "cafe.bar.espresso-filter": { img: "solutions-home-recipe", w: 1100, h: 880, pos: "60% center" },
    "cafe.bar.bean-to-cup": { img: "office-machine-6", w: 701, h: 876, pos: "center" },
    "cafe.orders.milk": { icon: "milk" },
    "cafe.orders.black": { icon: "droplet" },
    "cafe.orders.filter": { icon: "filter" },
    "cafe.orders.mix": { icon: "shuffle" },
    "cafe.known.classic": { img: "taste2-chocolate", w: 1536, h: 1024, pos: "40% center" },
    "cafe.known.fruity": { img: "taste2-fruit", w: 1536, h: 1024, pos: "45% center" },
    "cafe.known.bold": { img: "taste2-bold", w: 1536, h: 1024, pos: "57% center" },
    "cafe.known.rotating": { img: "taste2-rotating", w: 1536, h: 1024, pos: "50% center" },
  };

  const visualFor = (journey, question, answer) => VISUALS[`${question === "for" ? "who" : journey}.${question}.${answer}`] || {};

  /* ================================================================== Tiles.tsx */

  /** Every answer in a question uses the same kind of tile, decided by its first answer. */
  const kindOf = (v) => (v.img ? "photo" : v.icon ? "icon" : "type");

  /** Columns for a set of tiles on desktop. Phones always stack them as rows. */
  const gridFor = (count, kind) =>
    `grid gap-3 md:gap-4 ${count >= 5 ? "md:grid-cols-5" : count === 4 ? (kind === "type" || kind === "icon" ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-4") : count === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`;

  const TILE_BASE = "group relative h-full w-full overflow-hidden text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-4 focus-visible:ring-offset-cosmos";

  function mark(on, multi) {
    if (!on && !multi) return "";
    return `<span class="absolute right-3 top-3 z-20 flex h-7 w-7 items-center justify-center border transition ${multi ? "rounded-lg" : "rounded-full"} ${on ? "border-gold bg-gold text-[#16202a]" : "border-white/40 bg-black/25"}" aria-hidden="true">${on ? icon("check", "h-4 w-4", 2.4) : ""}</span>`;
  }
  const key = (n) =>
    `<span class="absolute left-3 top-3 z-20 hidden h-6 min-w-6 items-center justify-center rounded-md border border-white/25 bg-black/30 px-1.5 text-[0.7rem] text-white/80 backdrop-blur-sm md:flex" aria-hidden="true">${n}</span>`;
  /** Gold outline drawn above the photo when the answer is chosen. */
  const ring = (on) => (on ? `<span class="pointer-events-none absolute inset-0 z-10 rounded-[inherit] border-2 border-gold" aria-hidden="true"></span>` : "");

  function tile({ v, label, hint, visual, kind, on, multi, n, src }) {
    const attrs = `type="button" data-finder-pick="${esc(v)}" data-focus-key="opt-${esc(v)}" aria-pressed="${on ? "true" : "false"}"`;
    if (kind === "photo" && visual.img) {
      return `<button ${attrs} class="${TILE_BASE} grid grid-cols-[4.75rem_1fr] items-center gap-4 rounded-[18px] border border-ivory/12 bg-ivory/[0.04] p-2 pr-12 md:block md:h-[min(44vh,25rem)] md:rounded-[22px] md:border-0 md:p-0">
        <span class="relative block aspect-square overflow-hidden rounded-[12px] md:absolute md:inset-0 md:aspect-auto md:rounded-none">
          <img src="${esc(src)}" alt="" width="${visual.w}" height="${visual.h}" loading="eager" decoding="async" class="absolute inset-0 h-full w-full object-cover transition duration-300 ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.05]" style="object-position: ${visual.pos || "center"}">
        </span>
        <span class="pointer-events-none absolute inset-0 hidden bg-gradient-to-t from-black/85 via-black/25 to-black/0 md:block" aria-hidden="true"></span>
        <span class="relative z-10 md:absolute md:inset-x-0 md:bottom-0 md:p-5 lg:p-6">
          <span class="block text-[1.05rem] leading-snug text-ivory md:font-display md:text-[1.55rem] md:leading-[1.08] md:text-white">${esc(label)}</span>
          ${hint ? `<span class="mt-1 block text-[0.85rem] text-ivory/75 md:text-white/70">${esc(hint)}</span>` : ""}
        </span>
        ${ring(on)}${key(n)}${mark(on, multi)}
      </button>`;
    }
    return `<button ${attrs} class="${TILE_BASE} flex items-center gap-4 rounded-[18px] border bg-ivory/[0.04] p-4 pr-12 md:flex-col md:items-start md:justify-between md:rounded-[22px] md:p-6 ${
      kind === "type" ? "md:min-h-[9.5rem] md:justify-end" : "md:min-h-[12rem]"
    } ${on ? "border-gold bg-gold/[0.08]" : "border-ivory/12 hover:border-gold/45 hover:bg-ivory/[0.07]"}">
      ${visual.icon ? icon(visual.icon, "h-7 w-7 shrink-0 text-gold md:mt-4 md:h-9 md:w-9", 1.25) : ""}
      <span class="block">
        <span class="block leading-snug text-ivory md:font-display ${kind === "type" ? "md:text-[2.1rem] md:leading-none" : "md:text-[1.4rem] md:leading-[1.12]"} text-[1.05rem]">${esc(label)}</span>
        ${hint ? `<span class="mt-1 block text-[0.85rem] text-ivory/75">${esc(hint)}</span>` : ""}
      </span>
      ${key(n)}${mark(on, multi)}
    </button>`;
  }

  /* ================================================================== coffees from Shopify */

  /* The launch catalogue's order (src/lib/coffees.ts). The app's sorts are stable, so equal scores
     keep this order; sorting the Shopify products the same way gives the same ties. */
  const LAUNCH_ORDER = ["las-palmas", "embrujo-shaman", "ethiopia-guji", "kenya-gondo-aa", "milestone", "pulse", "kolliberri", "meridian", "lamastus-geisha", "ponderosa-geisha", "altieri-geisha", "magnifico", "maestro", "brazil-sorocabana", "ethiopia-guji-espresso"];
  const normSize = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");
  const gramsOf = (size, weight) => {
    const m = normSize(size).match(/^([\d.]+)(kg|g)/);
    if (m) return parseFloat(m[1]) * (m[2] === "kg" ? 1000 : 1);
    return Number(weight) || 0;
  };
  const list = (v) => (Array.isArray(v) ? v.map((x) => String(x).trim()).filter(Boolean) : []);

  function prepareCoffees(raw) {
    return (raw || [])
      .map((p, i) => ({
        slug: p.slug,
        name: p.name || p.slug,
        url: p.url,
        subtitle: p.subtitle || "",
        origin: p.origin || "",
        composition: p.composition || "",
        notes: list(p.notes),
        methods: list(p.methods),
        milkFriendly: p.milkFriendly === true,
        flavour: ["chocolate", "fruit", "floral", "bold"].includes(p.flavour) ? p.flavour : undefined,
        bestFor: list(p.bestFor),
        image: p.image || "",
        srcset: p.srcset || "",
        imageWidth: p.imageWidth || 1400,
        imageHeight: p.imageHeight || 1400,
        // Packs smallest first, as the app sorts them (coffees.ts).
        packs: (p.packs || [])
          .map((v) => ({ id: v.id, size: v.size, grams: gramsOf(v.size, v.grams), price: Number(v.price) || 0, available: v.available === true, priceHtml: v.priceHtml || "" }))
          .sort((a, b) => a.grams - b.grams),
        _i: LAUNCH_ORDER.includes(p.slug) ? LAUNCH_ORDER.indexOf(p.slug) : LAUNCH_ORDER.length + i,
      }))
      .filter((c) => c.slug && c.packs.length)
      .sort((a, b) => a._i - b._i);
  }

  /** Only priced, in-stock coffees can be a result (journeys.ts rule 3, Journey.tsx `available`). */
  const availableCoffee = (c) => c.packs.some((p) => p.available && p.price > 0);

  /* Exposed for tests (node) and debugging: the rules engine only, no DOM. */
  const engine = { whoQuestion, journeys, questionsFor, labelOf, rank, alternativeTo, match, toQuery, fromQuery, prepareCoffees, availableCoffee, brewGuides, VISUALS };
  if (typeof window === "undefined") {
    if (typeof module !== "undefined") module.exports = engine;
    return;
  }

  /* ================================================================== CoffeeVisual.tsx */

  const TONES = {
    "Classic Espresso": "from-[#33241a] via-[#1f1610] to-[#100c08]",
    "Single Origin Espresso": "from-[#2c2a20] via-[#1b1a13] to-[#0e0d09]",
    "Filter Coffee": "from-[#233546] via-[#16222e] to-[#0c131b]",
    "Limited Micro-Lot": "from-[#3b3222] via-[#211b12] to-[#110e09]",
  };

  /** The product photo (or bundled pack shot); without one, the drawn Champions bag (snippet coffee-visual). */
  function coffeeVisual(c, sizes, priority, bag) {
    const loading = priority ? "eager" : "lazy";
    if (c.image) {
      return `<img src="${esc(c.image)}"${c.srcset ? ` srcset="${esc(c.srcset)}" sizes="${esc(sizes)}"` : ""} alt="${esc(c.name)} coffee bag" width="${c.imageWidth}" height="${c.imageHeight}" loading="${loading}"${priority ? ` fetchpriority="high"` : ""} decoding="async" class="absolute inset-0 h-full w-full object-cover">`;
    }
    const tone = TONES[c.composition] || "from-[#3a2717] via-[#22170f] to-[#120d09]";
    return `<div class="absolute inset-0 bg-gradient-to-b ${tone}">
      <div class="absolute inset-x-0 bottom-0 h-1/3 bg-[radial-gradient(ellipse_at_50%_100%,rgba(198,166,100,0.18),transparent_65%)]"></div>
      <div class="@container absolute left-1/2 top-[8%] h-[84%] -translate-x-1/2" style="aspect-ratio: 430 / 626">
        <div class="absolute bottom-[1%] left-[10%] h-[5%] w-[80%] rounded-[50%] bg-black/60 blur-md"></div>
        <img src="${esc(bag)}" alt="" loading="lazy" width="430" height="626" class="absolute inset-0 h-full w-full object-contain">
        <div class="absolute left-[7%] right-[10%] top-[57%] flex flex-col items-center text-center text-[#2b2419]">
          <svg viewBox="0 0 200 30" class="mb-[4%] w-[70%] text-gold-deep/70" aria-hidden="true"><path d="M0 26 L28 12 L44 20 L70 4 L96 18 L118 8 L146 22 L170 10 L200 26" fill="none" stroke="currentColor" stroke-width="1"/><path d="M0 29 L34 17 L52 24 L78 10 L104 22 L126 14 L152 26 L176 16 L200 29" fill="none" stroke="currentColor" stroke-width="0.7"/></svg>
          <span class="font-display text-[7.2cqw] uppercase leading-tight tracking-[0.12em]">${esc(c.name)}</span>
          <span class="mt-[3%] text-[3.3cqw] uppercase tracking-[0.25em] text-[#5b5040]">${esc(c.origin)}</span>
          <span class="mt-[6%] h-px w-[16%] bg-gold-deep/70"></span>
          <span class="mt-[6%] text-[2.8cqw] uppercase tracking-[0.3em] text-[#5b5040]">Specialty coffee</span>
        </div>
      </div>
    </div>`;
  }

  /* ================================================================== Journey.tsx + Result.tsx */

  const notFor = { home: "Not for home?", office: "Not for an office?", hotel: "Not for a hotel?", cafe: "Not for a café?" };
  /** Shared with the matching moment so both load the same copy of the bag photo. */
  const MATCH_SIZES = "(min-width: 768px) 40vw, 72vw";
  const CHIP = "shrink-0 rounded-full border border-ivory/15 px-3 py-1 text-[0.8rem] text-ivory/70 transition hover:border-gold/60 hover:text-ivory";
  const PREFIX = { home: "FYHM", office: "FYOF", hotel: "FYHT", cafe: "FYCF" };
  const NEXT_ON = "border-ink bg-ink text-[#fbf6ec]";
  const NEXT_OFF = "border-ink/20 text-[#4b4236] hover:border-ink/50";

  /** Drops answers to questions that no longer apply (e.g. "Do you grind?" after switching to bean-to-cup). */
  function prune(journey, a) {
    const ids = new Set(questionsFor(journey, a, true).map((q) => q.id));
    return Object.fromEntries(Object.entries(a).filter(([k]) => ids.has(k)));
  }
  const answered = (q, a) => (q.multi ? !!(a[q.id] && a[q.id].length) : !!a[q.id]);
  const complete = (journey, a) => !!journey && questionsFor(journey, a, true).every((q) => answered(q, a));

  function toTop() {
    if (window.scrollY < 40) return;
    window.scrollTo({ top: 0, behavior: reduceMotion() ? "auto" : "smooth" });
  }

  /** Replaces a container's markup and gives focus back to the element that had it (same data-focus-key). */
  function swap(el, html) {
    const active = document.activeElement;
    const focusKey = active && el.contains(active) ? active.getAttribute("data-focus-key") : null;
    el.innerHTML = html;
    if (focusKey) {
      const next = el.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`);
      next && next.focus({ preventScroll: true });
    }
  }

  class Finder {
    constructor(root) {
      this.root = root;
      let data = {};
      try {
        data = JSON.parse(root.querySelector("[data-finder-data]").textContent);
      } catch (e) {
        data = {};
      }
      this.data = data;
      this.images = data.images || {};
      this.links = data.links || {};
      this.coffees = prepareCoffees(data.coffees);
      const q = (s) => root.querySelector(s);
      this.el = {
        ask: q("[data-finder-ask]"),
        top: q("[data-finder-top]"),
        stage: q("[data-finder-stage]"),
        bottom: q("[data-finder-bottom]"),
        postedNote: q("[data-finder-posted-note]"),
        result: q("[data-finder-result]"),
        match: q("[data-finder-match]"),
        details: q("[data-finder-details]"),
        tabs: q("[data-finder-tabs]"),
        panel: q("[data-finder-panel]"),
        leadRoot: q("[data-finder-capture] [data-lead-root]"),
        form: q("[data-finder-capture] form"),
        copy: q("[data-finder-copy]"),
        mail: q("[data-finder-mail]"),
      };
      this.posted = !!q("[data-lead-posted]");

      // State (Journey.tsx), first read from the address.
      const params = new URLSearchParams(window.location.search);
      const init = fromQuery(params);
      this.journey = init.journey;
      this.answers = init.answers;
      this.skipWho = init.skipWho && !!init.journey && init.journey !== "event";
      this.phase = params.get("done") === "1" && init.journey !== "event" && complete(init.journey, init.answers) ? "result" : "ask";
      // Arriving with ?for=office from the shop starts at question 2 with question 1 answered; from a Solutions page question 1 is skipped.
      this.step = this.journey && !this.skipWho && this.journey !== "event" ? 1 : 0;
      this.dir = 1;
      /** True after "change an answer": once nothing is missing, the next answer goes straight back to the result. */
      this.editing = false;
      this.result = null;
      this.tab = null;
      this.buys = {};
      this.stageKey = root.querySelector("[data-finder-ssr]") ? "q:for" : null;
      this.stageToken = 0;
      this.finishToken = 0;
      this.next = "cupping";

      this.onClick = this.onClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onLead = this.onLead.bind(this);
      root.addEventListener("click", this.onClick);
      root.addEventListener("champions:lead", this.onLead);
      window.addEventListener("keydown", this.onKey);

      if (this.posted && this.phase === "ask" && this.el.postedNote) this.el.postedNote.hidden = false;
      this.render();
      if (init.journey) track("journey_start", { journey: init.journey, entry: init.skipWho ? "solutions" : "link" });
    }

    destroy() {
      this.root.removeEventListener("click", this.onClick);
      this.root.removeEventListener("champions:lead", this.onLead);
      window.removeEventListener("keydown", this.onKey);
    }

    /* ---------- derived state ---------- */
    get j() {
      return this.journey && this.journey !== "event" ? this.journey : undefined;
    }
    get list() {
      return this.j ? questionsFor(this.j, this.answers, this.skipWho) : [whoQuestion];
    }
    get current() {
      return this.list[this.step];
    }
    coffee(slug) {
      return this.coffees.find((c) => c.slug === slug);
    }
    computeResult() {
      return this.j ? match(this.j, this.answers, this.coffees, availableCoffee) : null;
    }
    resultLink() {
      return `${window.location.origin}${window.location.pathname}?${toQuery(this.j, this.answers, this.skipWho)}&done=1`;
    }

    /* ---------- actions (Journey.tsx) ---------- */
    go(to) {
      this.dir = to >= this.step ? 1 : -1;
      this.step = to;
      toTop();
      this.render();
    }

    finish(a) {
      this.editing = false;
      this.phase = "matching";
      toTop();
      track("journey_complete", { journey: this.j, ...Object.fromEntries(Object.entries(a).map(([k, v]) => [k, Array.isArray(v) ? v.join(",") : v])) });
      this.render();
      const token = ++this.finishToken;
      window.setTimeout(() => {
        if (token !== this.finishToken || this.phase !== "matching") return;
        this.phase = "result";
        this.render();
      }, reduceMotion() ? 300 : 1300);
    }

    advance(jj, a, from) {
      const next = questionsFor(jj, a, this.skipWho);
      if (this.editing) {
        const missing = next.findIndex((q) => q.id !== "for" && !answered(q, a));
        return missing < 0 ? this.finish(a) : this.go(missing);
      }
      if (from + 1 < next.length) this.go(from + 1);
      else this.finish(a);
    }

    choose(q, v) {
      if (q.id === "for") {
        const who = v;
        const same = who === this.journey;
        const editing = this.editing;
        this.journey = who;
        track("journey_start", { journey: who, entry: "question" });
        if (who === "event") return this.render();
        const a = same ? this.answers : {};
        if (!same) {
          this.answers = {};
          this.editing = false;
        }
        this.render();
        window.setTimeout(() => (same && editing && complete(who, a) ? this.finish(a) : this.go(1)), 260);
        return;
      }
      const j = this.j;
      if (!j) return;
      const a = prune(j, { ...this.answers, [q.id]: v });
      const from = this.step;
      this.answers = a;
      track("journey_answer", { journey: j, question: q.id, answer: v });
      this.render();
      window.setTimeout(() => this.advance(j, a, from), 260);
    }

    toggle(q, v) {
      const cur = Array.isArray(this.answers[q.id]) ? this.answers[q.id] : [];
      this.answers = { ...this.answers, [q.id]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
      this.render();
    }

    continueMulti() {
      const cur = this.current;
      if (!this.j || !cur || !answered(cur, this.answers)) return;
      track("journey_answer", { journey: this.j, question: cur.id, answer: (this.answers[cur.id] || []).join(",") });
      this.advance(this.j, this.answers, this.step);
    }

    reset() {
      this.finishToken++;
      this.editing = false;
      this.journey = undefined;
      this.answers = {};
      this.skipWho = false;
      this.phase = "ask";
      this.dir = -1;
      this.step = 0;
      toTop();
      this.render();
    }

    changeAnswer(id) {
      const i = this.list.findIndex((q) => q.id === id);
      this.phase = "ask";
      this.editing = true;
      this.dir = -1;
      this.step = i < 0 ? 0 : i;
      toTop();
      track("journey_change_answer", { journey: this.j, question: id });
      this.render();
    }

    /* ---------- events ---------- */
    onClick(e) {
      const t = (sel) => e.target.closest(sel);
      let el;
      if ((el = t("[data-finder-pick]")) && this.phase === "ask") {
        const q = this.current;
        if (!q) return;
        const v = el.getAttribute("data-finder-pick");
        return q.multi ? this.toggle(q, v) : this.choose(q, v);
      }
      if ((el = t("[data-finder-chip]"))) {
        const id = el.getAttribute("data-finder-chip");
        return id === "for" ? this.reset() : this.changeAnswer(id);
      }
      if (t("[data-finder-back]")) return this.go(this.step - 1);
      if (t("[data-finder-reset]")) return this.reset();
      if (t("[data-finder-continue]")) return this.continueMulti();
      if ((el = t("[data-finder-tab]"))) {
        this.tab = el.getAttribute("data-finder-tab");
        track("journey_tab", { journey: this.j, tab: this.tab });
        return this.renderDetails(true);
      }
      if ((el = t("[data-buy-size]"))) {
        const box = el.closest("[data-buy]");
        const b = box && this.buys[box.getAttribute("data-buy")];
        if (!b) return;
        b.chosen = el.getAttribute("data-buy-size");
        return this.refreshBuy(box.getAttribute("data-buy"));
      }
      if ((el = t("[data-buy-add]"))) return this.addToCart(el);
      if ((el = t("[data-finder-copy]"))) return this.copyLink();
      if (t("[data-finder-mail]")) return track("journey_share", { journey: this.j, method: "email" });
      if ((el = t("[data-finder-scroll]"))) {
        const target = document.getElementById(el.getAttribute("data-finder-scroll"));
        target && target.scrollIntoView({ behavior: reduceMotion() ? "auto" : "smooth", block: "start" });
        return;
      }
      if ((el = t("[data-track-view]"))) return track("journey_view_coffee", { journey: this.j, coffee: el.getAttribute("data-track-view") });
      if ((el = t("[data-finder-next]"))) {
        this.next = el.getAttribute("data-finder-next");
        return this.renderNext();
      }
      if (t("[data-finder-event]")) track("journey_event_enquiry");
    }

    // Number keys choose an answer; Enter continues a pick-several question.
    onKey(e) {
      const cur = this.current;
      if (this.phase !== "ask" || !cur || this.journey === "event") return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (this.el.ask.hidden || !this.el.ask.offsetParent) return;
      const n = Number(e.key);
      if (n >= 1 && n <= cur.options.length) {
        e.preventDefault();
        const o = cur.options[n - 1];
        if (cur.multi) this.toggle(cur, o.v);
        else this.choose(cur, o.v);
      } else if (e.key === "Enter" && cur.multi) {
        if (t && t.closest && t.closest("button, a")) return; // Enter on a focused tile presses it
        this.continueMulti();
      }
    }

    onLead(e) {
      const d = e.detail || {};
      if (d.kind !== "journey") return;
      const ref = this.root.querySelector("[data-finder-sent-ref]");
      if (ref) ref.hidden = !d.reference;
      const consent = this.el.form && this.el.form.querySelector("[data-marketing-consent]");
      track("generate_lead", { form: "journey" });
      track("journey_lead_submit", { journey: this.j, priority: !!(this.result && this.result.priority), marketing: !!(consent && consent.checked) });
    }

    /* ---------- rendering ---------- */
    syncUrl() {
      const q = toQuery(this.journey, this.answers, this.skipWho);
      let s = q;
      if (this.phase === "result") s += `${q ? "&" : ""}done=1`;
      const keep = new URLSearchParams(window.location.search);
      for (const k of Array.from(keep.keys())) if (OWN_KEYS.has(k)) keep.delete(k);
      const extra = keep.toString();
      if (extra) s = s ? `${s}&${extra}` : extra;
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${s ? `?${s}` : ""}`);
    }

    render() {
      if (this.rendered && this.el.postedNote) this.el.postedNote.hidden = true;
      this.rendered = true;
      this.syncUrl();
      if (this.phase !== "ask") this.result = this.computeResult();
      if (this.phase === "result" && this.result && this.j) {
        this.el.ask.hidden = true;
        this.el.result.hidden = false;
        this.renderResult();
        return;
      }
      this.el.result.hidden = true;
      this.el.ask.hidden = false;
      if (this.phase === "ask") this.el.ask.setAttribute("aria-labelledby", "journey-q");
      else this.el.ask.removeAttribute("aria-labelledby");
      this.renderTop();
      this.renderStage();
      this.renderBottom();
    }

    /* Progress, and the answers so far (each one can be changed) */
    renderTop() {
      const box = this.el.top;
      if (this.phase !== "ask" || this.journey === "event") {
        box.innerHTML = "";
        return;
      }
      const j = this.j;
      const list = this.list;
      const total = j ? list.length + (j === "home" && !this.answers.brewer ? 1 : 0) : undefined;
      const count = total || 6;
      const width = (i) => (total && i < this.step ? "100%" : total && i === this.step ? "50%" : "0%");
      const before = j ? list.slice(0, this.step).filter((q) => q.id !== "for" || !this.skipWho) : [];
      const label = total ? `Question ${this.step + 1} of ${total}` : "Question 1";
      const chips = before
        .map((q) => {
          const text = q.id === "for" ? (whoQuestion.options.find((o) => o.v === this.journey) || {}).label : labelOf(q, this.answers[q.id]);
          return `<button type="button" data-finder-chip="${esc(q.id)}" data-focus-key="chip-${esc(q.id)}" class="${CHIP}" title="${esc(`Change: ${q.topic}`)}">${esc(text)}</button>`;
        })
        .join("");
      const bars = box.querySelectorAll("[data-bar]");
      if (bars.length === count) {
        bars.forEach((b, i) => (b.style.width = width(i)));
        box.querySelector("[data-finder-count]").textContent = label;
        swap(box.querySelector("[data-finder-chips]"), chips);
        return;
      }
      // Bars that were already there keep their width and animate from it (motion's initial={false}).
      const old = Array.from(bars).map((b) => b.style.width);
      const bar = (i) =>
        `<span class="h-[3px] flex-1 overflow-hidden rounded-full bg-ivory/12"><span data-bar class="block h-full bg-gold" style="width: ${old[i] || "0%"}; transition: width 0.4s ${EASE}"></span></span>`;
      swap(
        box,
        `<div class="flex items-center gap-4">
          <div class="flex flex-1 gap-1" aria-hidden="true">${Array.from({ length: count }, (_, i) => bar(i)).join("")}</div>
          <p class="shrink-0 text-[0.8rem] text-ivory/75" aria-live="polite" data-finder-count>${label}</p>
        </div>
        <div class="no-scrollbar -mx-5 mt-4 flex min-h-8 gap-2 overflow-x-auto px-5 md:mx-0 md:flex-wrap md:px-0" data-finder-chips>${chips}</div>`,
      );
      // Let the bars grow from their old width.
      requestAnimationFrame(() => box.querySelectorAll("[data-bar]").forEach((b, i) => (b.style.width = width(i))));
    }

    renderBottom() {
      const box = this.el.bottom;
      if (this.phase !== "ask" || this.journey === "event") {
        box.innerHTML = "";
        return;
      }
      const j = this.j;
      const cur = this.current;
      let left = "<span></span>";
      if (this.step > 0)
        left = `<button type="button" data-finder-back data-focus-key="back" class="flex items-center gap-2 py-2 text-ivory/65 transition hover:text-ivory">${icon("arrow-left", "h-4 w-4")} Back</button>`;
      else if (this.skipWho && j)
        left = `<button type="button" data-finder-reset data-focus-key="not-for" class="flex items-center gap-2 py-2 text-ivory/65 transition hover:text-ivory">${icon("arrow-left", "h-4 w-4")} ${esc(notFor[j])}</button>`;
      const hint = cur && cur.multi ? `Press 1 to ${cur.options.length} to pick, Enter to continue` : cur ? `Press 1 to ${cur.options.length} to choose` : "";
      const startOver =
        this.step > 0 || Object.keys(this.answers).length > 0
          ? `<button type="button" data-finder-reset data-focus-key="start-over" class="hidden items-center gap-2 py-2 text-ivory/50 transition hover:text-ivory sm:flex">${icon("rotate-ccw", "h-4 w-4")} Start over</button>`
          : "";
      const cont =
        cur && cur.multi
          ? `<button type="button" data-finder-continue data-focus-key="continue" ${answered(cur, this.answers) ? "" : "disabled"} class="btn btn-gold !py-3 text-sm disabled:pointer-events-none disabled:opacity-35">Continue ${icon("arrow-right", "h-4 w-4")}</button>`
          : "";
      swap(
        box,
        `<div class="flex items-center justify-between gap-4 border-t border-ivory/10 pt-5 text-sm">
          ${left}
          <p class="hidden text-[0.8rem] text-ivory/40 lg:block">${hint}</p>
          <div class="flex items-center gap-5">${startOver}${cont}</div>
        </div>`,
      );
    }

    stageView() {
      const reduce = reduceMotion();
      if (this.phase === "ask" && this.journey === "event") {
        return {
          key: "event",
          html: `<div class="max-w-2xl" data-view>
            <h2 id="journey-q" class="display text-[clamp(2.4rem,5vw,4.2rem)] leading-[1.02]">Planning an event? Let&rsquo;s talk it through.</h2>
            <p class="mt-6 max-w-xl text-lg leading-relaxed text-ivory/70">Every event is different, so there&rsquo;s no quiz for this one. Tell us the date, the guests and the space, and our events team will come back with a coffee bar plan.</p>
            <div class="mt-9 flex flex-wrap gap-3">
              <a href="${esc(this.links.events || "/pages/solutions-events#plan-your-space")}" class="btn btn-gold" data-finder-event>Plan your event coffee ${icon("arrow-right", "h-4 w-4")}</a>
              <button type="button" data-finder-reset data-focus-key="choose-again" class="btn btn-ghost">Choose again</button>
            </div>
          </div>`,
          enter: (el) => animate(el, [{ opacity: 0, transform: "translateY(10px)" }, { opacity: 1, transform: "none" }], { duration: 400 }),
          exit: (el) => animate(el, [{ opacity: 1 }, { opacity: 0 }], { duration: 400 }),
        };
      }
      const cur = this.current;
      if (this.phase === "ask" && cur) {
        const j = this.j;
        const value = cur.id === "for" ? this.journey : this.answers[cur.id];
        const kind = kindOf(visualFor(j, cur.id, cur.options[0].v));
        const shift = reduce ? 0 : 48 * this.dir;
        const tiles = cur.options
          .map((o, i) => {
            const visual = visualFor(j, cur.id, o.v);
            return `<div class="h-full" data-opt>${tile({
              v: o.v,
              label: o.label,
              hint: o.hint,
              visual,
              kind,
              on: Array.isArray(value) ? value.includes(o.v) : value === o.v,
              multi: cur.multi,
              n: i + 1,
              src: visual.img ? this.images[visual.img] : "",
            })}</div>`;
          })
          .join("");
        return {
          key: cur.id === "for" ? "q:for" : `q:${j}-${cur.id}`,
          html: `<div data-view>
            <h2 id="journey-q" class="display max-w-4xl text-[clamp(2.1rem,4.6vw,4rem)] leading-[1.04]">${esc(cur.title)}</h2>
            ${cur.sub ? `<p class="mt-3 text-[1.02rem] text-ivory/75">${esc(cur.sub)}</p>` : ""}
            <div class="mt-8 md:mt-10 ${gridFor(cur.options.length, kind)}" role="group" aria-labelledby="journey-q">${tiles}</div>
          </div>`,
          enter: (el) => {
            animate(el, [{ opacity: 0, transform: `translateX(${shift}px)` }, { opacity: 1, transform: "none" }], { duration: 420 });
            if (!reduce)
              $$("[data-opt]", el).forEach((o, i) => animate(o, [{ opacity: 0, transform: "translateY(18px)" }, { opacity: 1, transform: "none" }], { duration: 400, delay: Math.min(0.06 + i * 0.05, 0.15) * 1000 }));
          },
          exit: (el) => animate(el, [{ opacity: 1, transform: "none" }, { opacity: 0, transform: `translateX(${-(reduce ? 0 : 48 * this.dir)}px)` }], { duration: 420 }),
        };
      }
      if (this.phase === "matching" && this.result && this.result.primary) {
        const c = this.coffee(this.result.primary.slug);
        const lines = this.result.summary.filter(Boolean);
        return {
          key: "matching",
          // The moment between the last answer and the result: the match's bag lands, then the result opens.
          html: `<div class="flex flex-col items-center text-center" role="status" data-view>
            <div class="relative">
              <span data-ring class="absolute -inset-4 rounded-full border border-gold/50" aria-hidden="true"></span>
              <div class="relative h-44 w-44 overflow-hidden md:h-56 md:w-56" style="border-radius: 999px">${c ? coffeeVisual(c, MATCH_SIZES, true, this.data.bag) : ""}</div>
            </div>
            <p class="display mt-10 text-[clamp(1.8rem,3.4vw,2.6rem)]">Finding your match</p>
            <ul class="mt-5 space-y-2 text-[0.95rem] text-ivory/70">
              ${lines.map((s) => `<li data-line class="flex items-center justify-center gap-2">${icon("check", "h-4 w-4 text-gold")} ${esc(capitalise(s))}</li>`).join("")}
            </ul>
          </div>`,
          enter: (el) => {
            animate(el, [{ opacity: 0 }, { opacity: 1 }], { duration: 300 });
            animate(el.querySelector("[data-ring]"), [{ transform: "scale(0.85)", opacity: 0 }, { transform: "scale(1.05)", opacity: 1 }, { transform: "scale(1)", opacity: 0.6 }], { duration: 400 });
            $$("[data-line]", el).forEach((li, i) => animate(li, [{ opacity: 0, transform: "translateY(6px)" }, { opacity: 1, transform: "none" }], { duration: 300, delay: Math.min(0.2 + i * 0.22, 0.15) * 1000 }));
          },
          exit: () => Promise.resolve(),
        };
      }
      return { key: "none", html: "", enter: () => {}, exit: () => Promise.resolve() };
    }

    renderStage() {
      const stage = this.el.stage;
      const view = this.stageView();
      if (view.key === this.stageKey && !this.exiting) {
        if (stage.querySelector("[data-finder-ssr]") && view.key === "q:for") {
          stage.querySelector("[data-finder-ssr]").removeAttribute("data-finder-ssr");
          if (!this.journey) return; // the server-rendered first question is already right
        }
        swap(stage, view.html);
        return;
      }
      const prevExit = this.prevExit;
      this.stageKey = view.key;
      this.prevExit = view.exit;
      const token = ++this.stageToken;
      const show = () => {
        if (token !== this.stageToken) return;
        this.exiting = false;
        stage.innerHTML = view.html;
        const el = stage.firstElementChild;
        if (el) view.enter(el);
      };
      const old = stage.firstElementChild;
      if (this.exiting) {
        this.exitDone.then(show);
      } else if (old && prevExit) {
        this.exiting = true;
        this.exitDone = prevExit(old);
        this.exitDone.then(show);
      } else show();
    }

    /* ---------- result (Result.tsx) ---------- */
    renderResult() {
      const r = this.result;
      const j = this.j;
      const business = j !== "home";
      const primary = r.primary;
      const c = primary ? this.coffee(primary.slug) : undefined;
      this.buys = {};
      this.tab = null;
      this.stageKey = null; // the question screen starts fresh when an answer is changed
      this.stageToken++;
      this.exiting = false;
      this.el.stage.innerHTML = "";

      if (!primary || !c) {
        this.el.details.hidden = true;
        this.el.match.innerHTML = `<section class="bg-cosmos text-ivory">
          <h1 class="sr-only">Find your coffee</h1>
          <div class="container-x flex min-h-[80svh] flex-col justify-center pt-28">
            <h2 class="display max-w-2xl text-[clamp(2.2rem,4.6vw,3.6rem)]">Nothing in our current range fits every answer.</h2>
            <p class="mt-5 max-w-lg text-ivory/70">Change an answer, or ask the team and we&rsquo;ll help you directly.</p>
            <div class="mt-8 flex flex-wrap gap-3">
              <button type="button" data-finder-reset class="btn btn-gold">Start over</button>
              <a href="${esc(this.links.contact || "/pages/help#contact")}" class="btn btn-ghost">Contact us</a>
            </div>
          </div>
        </section>`;
        return;
      }
      this.el.details.hidden = false;
      this.buys.primary = { slug: c.slug, size: r.size, role: primary.role, compact: false };

      const questions = questionsFor(j, this.answers, this.skipWho).filter((q) => q.id !== "for");
      const answeredChips = `<div class="flex items-start gap-4">
        <p class="hidden shrink-0 pt-1 text-[0.8rem] text-ivory/45 md:block">Your answers</p>
        <div class="no-scrollbar -mx-5 flex flex-1 gap-2 overflow-x-auto px-5 md:mx-0 md:flex-wrap md:px-0">
          ${!this.skipWho ? `<button type="button" data-finder-chip="for" class="${CHIP}" title="Start again with a different answer">${esc((whoQuestion.options.find((o) => o.v === j) || {}).label)}</button>` : ""}
          ${questions.map((q) => `<button type="button" data-finder-chip="${esc(q.id)}" class="${CHIP}" title="${esc(`Change: ${q.topic}`)}">${esc(labelOf(q, this.answers[q.id]))}</button>`).join("")}
        </div>
      </div>`;

      const action = business
        ? `<div class="flex flex-wrap gap-3">
            <button type="button" data-finder-scroll="journey-capture" class="btn btn-gold">${esc(r.offer.button)} ${icon("arrow-right", "h-4 w-4")}</button>
            <a href="${esc(c.url)}" class="btn btn-ghost" data-track-view="${esc(c.slug)}">View coffee</a>
          </div>`
        : `<div class="space-y-3">
            ${this.buyHtml("primary")}
            <a href="${esc(c.url)}" class="inline-block text-sm text-ivory/60 underline-offset-4 hover:text-ivory hover:underline">More about ${esc(c.name)}</a>
          </div>`;
      const notes = r.notes.length ? `<ul class="mt-5 space-y-1 text-[0.85rem] text-ivory/50">${r.notes.map((n) => `<li>${esc(n)}</li>`).join("")}</ul>` : "";

      let partner = "";
      const pc = r.partner && this.coffee(r.partner.slug);
      if (pc) {
        partner = `<div data-fade="0.58" class="order-3 mt-8 flex items-center gap-4 rounded-[20px] border border-ivory/12 bg-ivory/[0.04] p-3 pr-5">
          <div class="relative h-20 w-20 shrink-0 overflow-hidden rounded-[14px]">${coffeeVisual(pc, "80px", false, this.data.bag)}</div>
          <div class="min-w-0 flex-1">
            <p class="text-[0.8rem] text-gold-soft">Paired with: ${esc(r.partner.role.toLowerCase())}</p>
            <p class="display mt-1 text-[1.45rem] leading-tight">${esc(pc.name)}</p>
            <p class="mt-1 text-[0.85rem] leading-snug text-ivory/60">${esc(r.partner.why[0])}</p>
          </div>
          <a href="${esc(pc.url)}" class="hidden shrink-0 text-sm text-ivory/70 underline-offset-4 hover:text-ivory hover:underline sm:block" data-track-view="${esc(pc.slug)}">View</a>
        </div>`;
      }

      // First screen: the match, why it fits, and what to do next
      this.el.match.innerHTML = `<section class="relative overflow-hidden bg-cosmos text-ivory" aria-labelledby="journey-match-name">
        <h1 class="sr-only">Find your coffee: your match</h1>
        <div class="container-x relative max-w-[78rem] pb-14 pt-24 md:pb-20 md:pt-28">
          ${answeredChips}
          <div class="mt-8 grid items-center gap-8 md:mt-10 md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] md:gap-14 lg:gap-20">
            <div class="mx-auto w-[min(13rem,56vw)] md:w-full md:max-w-[30rem]">
              <div data-finder-bag class="relative aspect-square overflow-hidden shadow-[0_40px_80px_-40px_rgba(0,0,0,0.8)]" style="border-radius: 28px">${coffeeVisual(c, MATCH_SIZES, true, this.data.bag)}</div>
            </div>
            <div class="flex flex-col md:block">
              <p data-fade="0.15" class="text-[0.95rem] text-gold-soft">${esc(primary.role)}</p>
              <h2 data-fade="0.22" id="journey-match-name" class="display mt-2 text-[clamp(3rem,7vw,5.6rem)] leading-[0.95]">${esc(c.name)}</h2>
              <p data-fade="0.28" class="mt-3 text-ivory/55">${esc(c.subtitle)}</p>
              <p data-fade="0.34" class="mt-6 max-w-lg text-[1.1rem] leading-relaxed text-ivory/85">${esc(r.headline)}</p>
              <ul data-fade="0.4" class="order-2 mt-5 space-y-2.5">
                ${primary.why.map((w) => `<li class="flex gap-3 text-[0.98rem] leading-relaxed text-ivory/75">${icon("check", "mt-1 h-4 w-4 shrink-0 text-gold")} ${esc(w)}</li>`).join("")}
              </ul>
              <p data-fade="0.44" class="order-2 mt-4 text-[0.85rem] text-ivory/45">${esc(ROASTER)}</p>
              <div data-fade="0.5" class="order-1 mt-6 md:mt-8">${action}${notes}</div>
              ${partner}
            </div>
          </div>
        </div>
      </section>`;

      if (!reduceMotion()) {
        animate(this.el.match.querySelector("[data-finder-bag]"), [{ opacity: 0, transform: "scale(0.6)" }, { opacity: 1, transform: "none" }], { duration: 500 });
        $$("[data-fade]", this.el.match).forEach((el) =>
          animate(el, [{ opacity: 0, transform: "translateY(14px)" }, { opacity: 1, transform: "none" }], { duration: 400, delay: parseFloat(el.getAttribute("data-fade")) * 1000 }),
        );
      }

      this.renderDetails(false);
      this.setupCapture();
      this.setupShare();
      if (window.Champions && window.Champions.initReveal) window.Champions.initReveal(this.el.result);
    }

    /* ---------- buying (home) ---------- */
    buyHtml(id) {
      const b = this.buys[id];
      const c = b && this.coffee(b.slug);
      if (!c) return "";
      const sizes = c.packs.filter((p) => p.available);
      if (!b.chosen) {
        const pick = sizes.find((p) => normSize(p.size) === normSize(b.size)) || sizes[0];
        b.chosen = pick && pick.size;
      }
      const pack = sizes.find((p) => p.size === b.chosen);
      if (!pack) return "";
      const group =
        sizes.length > 1
          ? `<div class="flex gap-1.5 rounded-full border border-current/15 p-1" role="radiogroup" aria-label="${esc(`${c.name} pack size`)}">
              ${sizes
                .map(
                  (p) =>
                    `<button type="button" role="radio" aria-checked="${p.size === b.chosen ? "true" : "false"}" data-buy-size="${esc(p.size)}" data-focus-key="size-${esc(id)}-${esc(p.size)}" class="rounded-full px-3.5 py-1.5 text-sm transition ${p.size === b.chosen ? "bg-gold text-[#16202a]" : "opacity-70 hover:opacity-100"}">${esc(p.size)}</button>`,
                )
                .join("")}
            </div>`
          : "";
      return `<div class="flex flex-wrap items-center gap-3" data-buy="${esc(id)}">
        ${group}
        <button type="button" data-buy-add data-focus-key="add-${esc(id)}" class="btn btn-gold ${b.compact ? "!py-2.5 text-sm" : ""}">
          ${b.added ? `${icon("check", "h-4 w-4")} Added to cart` : `${icon("plus", "h-4 w-4")} Add to cart, ${pack.priceHtml || esc((pack.price / 100).toFixed(2))}`}
        </button>
      </div>`;
    }

    refreshBuy(id) {
      const box = this.root.querySelector(`[data-buy="${CSS.escape(id)}"]`);
      if (!box) return;
      const wrap = document.createElement("div");
      wrap.innerHTML = this.buyHtml(id);
      const next = wrap.firstElementChild;
      if (!next) return;
      const active = document.activeElement;
      const focusKey = active && box.contains(active) ? active.getAttribute("data-focus-key") : null;
      box.replaceWith(next);
      if (focusKey) {
        const f = next.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`);
        f && f.focus({ preventScroll: true });
      }
    }

    async addToCart(btn) {
      const box = btn.closest("[data-buy]");
      const id = box && box.getAttribute("data-buy");
      const b = id && this.buys[id];
      const c = b && this.coffee(b.slug);
      const pack = c && c.packs.find((p) => p.size === b.chosen && p.available);
      if (!pack || btn.getAttribute("aria-busy") === "true") return;
      const cart = window.Champions && window.Champions.cart;
      if (!cart || !cart.el) {
        // No drawer (or no theme script): Shopify's own add-to-cart address.
        window.location.href = `${(window.Champions && window.Champions.routes && window.Champions.routes.cartAdd) || "/cart/add"}?id=${encodeURIComponent(pack.id)}&quantity=1`;
        return;
      }
      btn.setAttribute("aria-busy", "true");
      try {
        await cart.add([{ id: Number(pack.id), quantity: 1 }]);
        b.added = true;
        track("journey_add_to_cart", { journey: this.j, coffee: c.slug, size: b.chosen, role: b.role });
        this.refreshBuy(id);
      } catch (err) {
        btn.removeAttribute("aria-busy");
        window.alert(err && err.message ? err.message : "Something went wrong. Please try again.");
      }
    }

    /* ---------- below the first screen ---------- */
    renderDetails(animateIn) {
      const r = this.result;
      const j = this.j;
      const business = j !== "home";
      const hasPlan = !!(r.equipment || (r.outlets && r.outlets.length) || r.volume);
      const tabs = [
        ...(hasPlan ? [{ id: "plan", label: business ? "Your plan" : "Getting started" }] : []),
        ...(r.alternative && this.coffee(r.alternative.slug) ? [{ id: "different", label: "Something different" }] : []),
        { id: "brew", label: "How to brew it" },
      ];
      if (!this.tab || !tabs.some((t) => t.id === this.tab)) this.tab = tabs[0].id;
      const tab = this.tab;
      swap(
        this.el.tabs,
        tabs
          .map(
            (t) =>
              `<button type="button" role="tab" id="tab-${t.id}" aria-selected="${tab === t.id ? "true" : "false"}" aria-controls="panel-${t.id}" data-finder-tab="${t.id}" data-focus-key="tab-${t.id}" class="relative shrink-0 rounded-full px-5 py-2.5 text-[0.95rem] transition ${tab === t.id ? "text-[#fbf6ec]" : "text-[#4b4236] hover:bg-ink/[0.05]"}">${tab === t.id ? `<span class="absolute inset-0 rounded-full bg-ink"></span>` : ""}<span class="relative">${esc(t.label)}</span></button>`,
          )
          .join(""),
      );
      let body = "";
      if (tab === "plan") body = this.planPanel(r);
      else if (tab === "different") body = this.differentPanel(r.alternative, j, r.size);
      else body = this.brewPanel(r);
      this.el.panel.innerHTML = `<div id="panel-${tab}" role="tabpanel" aria-labelledby="tab-${tab}">${body}</div>`;
      if (animateIn) animate(this.el.panel.firstElementChild, [{ opacity: 0, transform: "translateY(10px)" }, { opacity: 1, transform: "none" }], { duration: 300 });
    }

    planPanel(r) {
      const card = (c) => `<div class="rounded-[20px] border border-ink/10 bg-white/70 p-6">
        ${c.label ? `<p class="text-[0.85rem] text-gold-deep">${esc(c.label)}</p>` : ""}
        <p class="display mt-1 text-[1.6rem] leading-tight">${esc(c.title)}</p>
        <p class="mt-2 leading-relaxed text-[#4b4236]">${esc(c.body)}</p>
      </div>`;
      return `<div class="grid gap-8 lg:grid-cols-[minmax(0,4fr)_minmax(0,7fr)]">
        <div>
          <p class="display text-[clamp(1.8rem,3vw,2.5rem)] leading-[1.1]">${esc(r.volume || (r.equipment && r.equipment.title) || "")}</p>
          ${r.journey === "office" ? `<p class="mt-4 text-[0.9rem] text-[#7d6c52]">An estimate at 2 cups per person each working day. We confirm it with you in your plan.</p>` : ""}
          ${r.journey === "cafe" ? `<p class="mt-4 text-[0.9rem] text-[#7d6c52]">Based on the weekly volume you chose. We confirm it with you in your plan.</p>` : ""}
        </div>
        <div class="grid gap-4 sm:grid-cols-2">
          ${r.equipment ? card(r.equipment) : ""}
          ${(r.outlets || []).map(card).join("")}
        </div>
      </div>`;
    }

    differentPanel(pick, journey, size) {
      const c = this.coffee(pick.slug);
      if (!c) return "";
      if (journey === "home") this.buys.different = this.buys.different || { slug: c.slug, size, role: "Something different", compact: true };
      return `<div class="grid items-center gap-8 md:grid-cols-[minmax(0,3fr)_minmax(0,7fr)]">
        <div class="relative mx-auto aspect-square w-[min(15rem,70vw)] overflow-hidden rounded-[24px] md:w-full">${coffeeVisual(c, "(min-width: 768px) 25vw, 70vw", false, this.data.bag)}</div>
        <div>
          <p class="text-[0.95rem] text-gold-deep">From another flavour family, for when you want a change</p>
          <p class="display mt-2 text-[clamp(2.2rem,4vw,3.2rem)] leading-none">${esc(c.name)}</p>
          <p class="mt-2 text-[#7d6c52]">${esc(c.subtitle)}</p>
          <ul class="mt-5 space-y-2">
            ${pick.why.map((w) => `<li class="flex gap-3 leading-relaxed text-[#4b4236]">${icon("check", "mt-1 h-4 w-4 shrink-0 text-gold-deep")} ${esc(w)}</li>`).join("")}
          </ul>
          <div class="mt-6 flex flex-wrap items-center gap-4">
            ${journey === "home" ? this.buyHtml("different") : ""}
            <a href="${esc(c.url)}" class="text-sm text-[#4b4236] underline underline-offset-4 hover:text-ink" data-track-view="${esc(c.slug)}">View coffee</a>
          </div>
        </div>
      </div>`;
    }

    brewPanel(r) {
      const g = brewGuides[r.brew];
      const rows = [
        ["Dose", g.dose],
        ["Water / yield", g.water],
        ["Ratio", g.ratio],
        ["Temperature", g.temp],
        ["Grind", g.grind],
        ["Time", g.time],
      ];
      return `<div class="grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <div>
          <p class="display text-[clamp(1.8rem,3vw,2.5rem)] leading-[1.1]">${esc(g.name)}</p>
          <p class="mt-3 text-[#4b4236]">${esc(g.summary)}</p>
          <dl class="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-[18px] border border-ink/10 bg-ink/10 sm:grid-cols-3">
            ${rows.map(([k, v]) => `<div class="bg-[#fbf6ec] p-4"><dt class="text-[0.8rem] text-[#7d6c52]">${esc(k)}</dt><dd class="mt-1 text-[0.95rem]">${esc(v)}</dd></div>`).join("")}
          </dl>
          <p class="mt-4 text-[0.8rem] text-[#7d6c52]">General method guide.</p>
        </div>
        <ol class="space-y-4">
          ${g.steps.map((s, i) => `<li class="flex gap-4 leading-relaxed text-[#3b332a]"><span class="display w-7 shrink-0 text-[1.6rem] leading-none text-gold-deep">${i + 1}</span>${esc(s)}</li>`).join("")}
        </ol>
      </div>`;
    }

    /* ---------- contact capture: optional, the result is already theirs ---------- */
    setupCapture() {
      const r = this.result;
      const j = this.j;
      const business = j !== "home";
      const root = this.root.querySelector("[data-finder-capture]");
      const form = this.el.form;
      if (!root || !form) return;
      const set = (sel, text) => $$(sel, root).forEach((el) => (el.textContent = text));
      const opening = j === "hotel" && this.answers.stage === "opening";

      set("[data-finder-offer-title]", r.offer.title);
      set("[data-finder-offer-body]", r.offer.body);
      const included = [r.primary, r.partner]
        .filter(Boolean)
        .map((p) => (this.coffee(p.slug) || {}).name)
        .filter(Boolean);
      const items = [
        included.length ? `${business ? "Coffee" : "Your match"}: ${included.join(" and ")}` : "",
        r.volume || "",
        r.equipment ? r.equipment.title : "",
        r.outlets && r.outlets.length ? `Outlets: ${r.outlets.map((o) => o.title).join(", ")}` : "",
      ].filter(Boolean);
      // The whole journey, step 1 to the last, as the visitor answered it: shown here and saved.
      const value = (q) => (q.id === whoQuestion.id ? this.j : this.answers[q.id]);
      const answered = (q) => {
        const v = value(q);
        return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== "";
      };
      const steps = this.list.filter(answered).map((q, i) => ({ n: i + 1, q, answer: labelOf(q, value(q)) }));
      const journeyList = root.querySelector("[data-finder-journey-list]");
      if (journeyList)
        journeyList.innerHTML = steps
          .map(
            (s) =>
              `<li class="flex gap-3"><span class="display w-6 shrink-0 text-[1.3rem] leading-none text-gold-deep">${s.n}</span><span><span class="block text-[0.8rem] leading-snug text-[#7d6c52]">${esc(s.q.title)}</span><span class="block leading-snug text-ink">${esc(s.answer)}</span></span></li>`,
          )
          .join("");
      const inc = root.querySelector("[data-finder-included]");
      if (inc) {
        inc.hidden = !items.length;
        inc.querySelector("[data-finder-included-list]").innerHTML = items.map((x) => `<li>${esc(x)}</li>`).join("");
      }

      // Which fields this journey asks for (validation and the note skip hidden ones).
      $$("[data-finder-show]", form).forEach((el) => {
        const w = el.getAttribute("data-finder-show");
        el.hidden = !(w === "business" ? business : w === "home" ? !business : w === "opening" ? opening : w === "cafe" ? j === "cafe" : true);
      });
      set("[data-finder-email-label]", business ? "Work email" : "Email");
      const email = form.querySelector('[name="contact[email]"]');
      if (email) email.setAttribute("data-error", business ? "Please add a valid work email." : "Please add a valid email.");
      set("[data-finder-company-label]", j === "hotel" ? "Property or group" : j === "cafe" ? "Café or restaurant" : "Company");
      const submit = form.querySelector("[data-finder-submit-label]");
      if (submit) submit.textContent = r.offer.button;
      const btn = form.querySelector('[type="submit"]');
      if (btn) delete btn.dataset.label; // champions.js restores the label it saved; the label changed

      // What champions.js stores on the Shopify customer (as /api/journey did).
      form.dataset.leadPrefix = PREFIX[j];
      form.dataset.leadLabel = `Find Your Coffee (${journeys[j].name})`;
      const name = (slug) => (this.coffee(slug) || {}).name || slug;
      const field = (n, v) => {
        const el = form.querySelector(`[data-finder-field="${n}"]`);
        if (el) el.value = v || "";
      };
      field("journey", j);
      field("steps", String(steps.length));
      field(
        "match",
        [r.primary && `${r.primary.role}: ${name(r.primary.slug)}`, r.partner && `${r.partner.role}: ${name(r.partner.slug)}`, r.alternative && `Something different: ${name(r.alternative.slug)}`].filter(Boolean).join("\n"),
      );
      field("priority", r.priority ? "yes" : "no");
      field("equipment", r.equipment ? r.equipment.title : "");
      field("outlets", r.outlets ? r.outlets.map((o) => o.title).join(", ") : "");
      field("volume", r.volume || "");
      field("link", this.resultLink());
      // Tag groups: answer:<journey>-<question>-<answer> and match:<handle>, one checkbox each.
      const codes = journeys[j].questions.flatMap((q) => {
        const v = this.answers[q.id];
        return (Array.isArray(v) ? v : v ? [v] : []).map((x) => `${j}-${q.id}-${x}`);
      });
      const handles = [r.primary && r.primary.slug, r.partner && r.partner.slug].filter(Boolean);
      const box = form.querySelector("[data-finder-tag-inputs]");
      if (box) {
        const cb = (n, label, tag, v) => `<input type="checkbox" tabindex="-1" checked name="${n}" value="${esc(v)}" data-lead-field="${label}" data-lead-tag="${tag}">`;
        // One field per step ("Step 2: How do you make coffee at home?" = "Espresso machine").
        const step = (st) =>
          `<input type="hidden" name="step_${st.n}" value="${esc(st.answer)}" data-lead-field="Step ${st.n}: ${esc(st.q.title)}" data-lead-key="step-${st.n}-${esc(st.q.id)}">`;
        box.innerHTML =
          steps.map(step).join("") + codes.map((v) => cb("answer_codes", "Answer codes", "answer", v)).join("") + handles.map((v) => cb("match_handles", "Matched coffees", "match", v)).join("");
      }
      this.next = "cupping";
      this.renderNext();

      // A fresh form for every result (the app remounts it), unless Shopify just confirmed a post.
      if (this.posted) {
        this.posted = false;
      } else {
        $$("[data-lead-fields]", this.el.leadRoot || root).forEach((el) => (el.hidden = false));
        $$("[data-lead-success]", this.el.leadRoot || root).forEach((el) => (el.hidden = true));
        $$("[data-lead-status]", form).forEach((el) => (el.textContent = ""));
      }
      set("[data-finder-sent-title]", business ? "Sent. Our team will be in touch shortly." : "Sent. We'll be in touch with your recipe.");
      set("[data-finder-sent-body]", business ? "We'll prepare your plan and arrange a tasting." : "Check your inbox in a few minutes.");
    }

    renderNext() {
      const form = this.el.form;
      if (!form) return;
      const labels = { cupping: "Cupping at the roastery", samples: "Sample pack" };
      const input = form.querySelector('[data-finder-field="next"]');
      if (input) input.value = this.j === "cafe" ? labels[this.next] || "" : "";
      $$("[data-finder-next]", form).forEach((b) => {
        const on = b.getAttribute("data-finder-next") === this.next;
        b.setAttribute("aria-checked", on ? "true" : "false");
        b.className = `rounded-full border px-4 py-2 text-sm transition ${on ? NEXT_ON : NEXT_OFF}`;
      });
    }

    setupShare() {
      const business = this.j !== "home";
      const link = this.resultLink();
      const first = this.result.primary ? (this.coffee(this.result.primary.slug) || {}).name || "" : "";
      if (this.el.mail) {
        this.el.mail.href = `mailto:?subject=${encodeURIComponent(business ? `Our coffee plan: ${first}` : `A coffee for you: ${first}`)}&body=${encodeURIComponent(`${this.result.headline}\n\nFrom Champions Coffee:\n${link}`)}`;
        const label = this.el.mail.querySelector("[data-finder-mail-label]");
        if (label) label.textContent = business ? "Send this plan to a colleague" : "Send to a friend";
      }
      this.setCopied(false);
    }

    setCopied(on) {
      if (!this.el.copy) return;
      this.el.copy.innerHTML = `${icon(on ? "check" : "copy", "h-4 w-4")} ${on ? "Link copied" : "Copy result link"}`;
    }

    async copyLink() {
      const link = this.resultLink();
      try {
        await navigator.clipboard.writeText(link);
        this.setCopied(true);
        window.clearTimeout(this.copyTimer);
        this.copyTimer = window.setTimeout(() => this.setCopied(false), 2000);
      } catch (e) {
        window.prompt("Copy this link", link);
      }
      track("journey_share", { journey: this.j, method: "copy" });
    }
  }

  /* ---------- boot ---------- */
  const instances = new Map();
  function init(scope) {
    $$("[data-finder]", scope).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new Finder(root));
    });
  }
  window.ChampionsFinder = Object.assign(engine, { init });

  const start = () => init(document);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
  document.addEventListener("shopify:section:load", (e) => init(e.target));
  document.addEventListener("shopify:section:unload", (e) => {
    $$("[data-finder]", e.target).forEach((root) => {
      const f = instances.get(root);
      if (f) f.destroy();
      instances.delete(root);
    });
  });
})();
